/* ==========================================================================
   NOCTURYL 2.0 — theme engine
   Modules attach to [data-module="name"] elements and re-run when a section
   is (re)loaded in the theme editor. Animations only run when GSAP is present,
   motion is enabled in theme settings and the visitor hasn't asked for
   reduced motion; everything is readable and usable without them.
   ========================================================================== */
(() => {
  'use strict';
  const T = window.theme || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const html = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(hover:hover) and (pointer:fine)').matches;
  // phones and tablets: canvases draw at 30fps and a lower resolution (looks the same, runs far cooler)
  const lowPower = matchMedia('(hover:none),(pointer:coarse)').matches;
  const FRAME_MS = lowPower ? 1000 / 30 : 0;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const hasGsap = () => !!(window.gsap && window.ScrollTrigger);
  const motionOn = () => hasGsap() && T.motion !== false && !reduce;
  // per-section motion settings written by snippets/section-attrs.liquid
  const motionOf = (el) => {
    const s = (el && el.closest && (el.closest('[data-anim]') || el.closest('[data-section]'))) || {};
    const d = s.dataset || {};
    return { anim: d.anim || 'default', speed: Math.max(0.3, (+d.speed || 100) / 100), stagger: (d.stagger != null ? +d.stagger : 80) / 1000 };
  };
  const store = {
    get(k) { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } },
  };
  const money = (cents) => {
    const fmt = T.moneyFormat || '${{amount}}';
    const v = (cents / 100).toFixed(2);
    const withCommas = (n) => n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      const [i, d] = v.split('.');
      if (k === 'amount_no_decimals') return withCommas(i);
      if (k === 'amount_with_comma_separator') return withCommas(i).replace(/,/g, '.') + ',' + d;
      if (k === 'amount_no_decimals_with_comma_separator') return withCommas(i).replace(/,/g, '.');
      return withCommas(i) + '.' + d;
    });
  };
  const commas = (n) => Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const NX = (window.NX = { $, $$, money, clamp, store, modules: {} });

  /* ---------- toast ---------- */
  NX.toast = (msg) => {
    const t = $('#Toast'); if (!t) return;
    t.textContent = msg; t.hidden = false; t.classList.add('show');
    clearTimeout(t._t); t._t = setTimeout(() => { t.classList.remove('show'); setTimeout(() => (t.hidden = true), 400); }, 2800);
  };

  /* ---------- smooth scroll ---------- */
  let lenis = null;
  function setupScroll() {
    if (hasGsap()) gsap.registerPlugin(ScrollTrigger);
    if (window.Lenis && T.smooth && !reduce && !T.designMode) {
      lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 0.95 });
      if (hasGsap()) { lenis.on('scroll', ScrollTrigger.update); gsap.ticker.add((t) => lenis.raf(t * 1000)); gsap.ticker.lagSmoothing(0); }
      else { const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); }; requestAnimationFrame(raf); }
    }
    NX.lenis = lenis;
  }
  NX.scrollTo = (target, opts = {}) => {
    if (lenis) return lenis.scrollTo(target, { duration: 1.4, easing: (t) => 1 - Math.pow(1 - t, 4), offset: -20, ...opts });
    const y = typeof target === 'number' ? target : target.getBoundingClientRect().top + scrollY + (opts.offset ?? -20);
    scrollTo({ top: y, behavior: reduce ? 'auto' : 'smooth' });
  };
  // blurred backdrop behind pop-outs; z sits just under the pop-out's own layer
  NX.scrim = (() => { let s; return (on, z, onClick) => { if (!s) { s = document.createElement('div'); s.className = 'nx-scrim'; document.body.appendChild(s); s.addEventListener('click', () => s._cb && s._cb()); } s._cb = onClick; if (z) s.style.zIndex = z; s.classList.toggle('on', !!on); }; })();
  NX.lock = (on) => { if (lenis) on ? lenis.stop() : lenis.start(); html.style.overflow = on ? 'hidden' : ''; };
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href*="#"]');
    if (!a || a.target === '_blank') return;
    const u = new URL(a.href, location.href);
    if (u.pathname !== location.pathname || !u.hash || u.hash === '#') return;
    const el = document.getElementById(decodeURIComponent(u.hash.slice(1)));
    if (!el) return;
    e.preventDefault(); closeMenu(); NX.scrollTo(el);
  });

  /* ---------- liquid helpers ---------- */
  NX.waveSVG = (amp = 9, cls = 'wave') => {
    const ns = 'http://www.w3.org/2000/svg', svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 2400 60'); svg.setAttribute('preserveAspectRatio', 'none'); svg.setAttribute('class', cls); svg.setAttribute('aria-hidden', 'true');
    let d = 'M0,60 L0,30';
    for (let x = 0; x <= 2400; x += 20) d += ` L${x},${(30 - amp * Math.sin((x / 600) * Math.PI * 2)).toFixed(1)}`;
    const p = document.createElementNS(ns, 'path'); p.setAttribute('d', d + ' L2400,60 Z'); svg.appendChild(p);
    return svg;
  };
  NX.setLevel = (tube, v) => tube.style.setProperty('--level', clamp(v, 0, 1).toFixed(4));
  NX.liquidColor = (t) => {
    t = clamp(t, 0, 1);
    const S = [[35, 86, 67], [88, 40, 60], [162, 32, 56]], k = t < 0.5 ? 0 : 1, f = (t - k * 0.5) / 0.5, a = S[k], b = S[k + 1];
    const v = a.map((x, i) => (x + (b[i] - x) * f).toFixed(0));
    return `hsl(${v[0]} ${v[1]}% ${v[2]}%)`;
  };
  function initTubes(root) {
    $$('.tube', root).forEach((t) => {
      const fill = $('.tube-fill', t);
      if (fill && !$('.wave', fill)) { fill.appendChild(NX.waveSVG(16, 'wave b')); fill.appendChild(NX.waveSVG(11, 'wave')); }
      if (t.dataset.level && !t.dataset.fillOnView) NX.setLevel(t, +t.dataset.level);
    });
  }

  /* ---------- text split + motion ---------- */
  function splitWords(el, mode) {
    const walk = (node) => {
      Array.from(node.childNodes).forEach((n) => {
        if (n.nodeType === 3) {
          const frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach((tok) => {
            if (!tok) return;
            if (/^\s+$/.test(tok)) { frag.appendChild(document.createTextNode(' ')); return; }
            if (mode === 'scrub') { const s = document.createElement('span'); s.className = 'sw'; s.textContent = tok; frag.appendChild(s); }
            else { const w = document.createElement('span'); w.className = 'w'; const i = document.createElement('span'); i.className = 'wi'; i.textContent = tok; w.appendChild(i); frag.appendChild(w); }
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1 && n.tagName !== 'BR') walk(n);
      });
    };
    walk(el);
  }
  function initMotion(root) {
    $$('[data-count]', root).forEach((el) => {
      if (el._nx) return; el._nx = 1;
      const mm = String(el.dataset.count).match(/^([^\d-]*)(-?[\d,]*\.?\d+)(.*)$/);
      if (!mm) return;
      const raw = mm[2].replace(/,/g, ''), end = parseFloat(raw), pre = el.dataset.prefix || mm[1], suf = el.dataset.suffix || mm[3];
      const dec = (raw.split('.')[1] || '').length;
      if (isNaN(end)) return;
      const write = (v) => (el.textContent = pre + (dec ? v.toFixed(dec) : commas(v)) + suf);
      if (!motionOn()) return write(end);
      write(0);
      ScrollTrigger.create({ trigger: el, start: 'top 92%', once: true, onEnter: () => { const o = { v: 0 }; gsap.to(o, { v: end, duration: 2.2, ease: 'expo.out', onUpdate: () => write(o.v) }); } });
    });
    if (!motionOn() || T.designMode) { $$('[data-split]', root).forEach((e) => e.classList.add('is-split')); return; }
    $$('[data-split]', root).forEach((el) => {
      if (el._nx) return; el._nx = 1;
      const m = motionOf(el);
      if (m.anim === 'none') { el.classList.add('is-split'); return; }
      splitWords(el);
      const words = $$('.wi', el);
      gsap.set(words, { yPercent: 115, rotate: 3 });
      el.classList.add('is-split');
      ScrollTrigger.create({ trigger: el, start: 'top 90%', once: true, onEnter: () => gsap.to(words, { yPercent: 0, rotate: 0, duration: 1.2 / m.speed, ease: 'expo.out', stagger: 0.045 / m.speed }) });
    });
    const reveal = $$('[data-reveal]', root).filter((e) => !e._nx);
    reveal.forEach((e) => (e._nx = 1));
    if (reveal.length) ScrollTrigger.batch(reveal, { start: 'top 94%', once: true, onEnter: (els) => {
      // one tween per section so each section's Design > motion settings apply
      const groups = new Map();
      els.forEach((e) => { const sec = e.closest('[data-anim]') || e.closest('[data-section]') || document.body; if (!groups.has(sec)) groups.set(sec, []); groups.get(sec).push(e); });
      groups.forEach((list, sec) => {
        const m = motionOf(sec);
        if (m.anim === 'none') { list.forEach((e) => e.classList.add('is-in')); return; }
        const to = { opacity: 1, x: 0, y: 0, scale: 1, rotationX: 0, duration: 1.1 / m.speed, ease: 'expo.out', stagger: m.stagger, overwrite: true,
          onComplete() { list.forEach((e) => { e.classList.add('is-in'); gsap.set(e, { clearProps: 'opacity,transform,filter' }); }); } };
        if (m.anim === 'blur') to.filter = 'blur(0px)';
        gsap.to(list, to);
      });
    } });
    // Design > "Highlighter on italic words": draw the marker when the section comes into view
    $$('[data-hfx="highlight"]', root).forEach((sec) => { if (sec._hl) return; sec._hl = 1; ScrollTrigger.create({ trigger: sec, start: 'top 75%', once: true, onEnter: () => sec.classList.add('hl-on') }); });
    // Design > "Parallax on images"
    $$('[data-dpx]', root).forEach((sec) => {
      if (sec._px) return; sec._px = 1;
      $$('img', sec).filter((img) => !img.closest('[data-parallax],.rv-avatars,.pd-thumbs,moonbundle-component,.logo,.ft-logo') && img.getBoundingClientRect().width > 160).forEach((img) => {
        gsap.set(img, { scale: 1.14 });
        gsap.fromTo(img, { yPercent: -6 }, { yPercent: 6, ease: 'none', scrollTrigger: { trigger: img.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } });
      });
    });
    // Design > card hover "3D tilt" (mouse only)
    if (fine) $$('[data-hover="tilt"]', root).forEach((sec) => {
      if (sec._tilt) return; sec._tilt = 1;
      $$('article,.card,.product-card', sec).filter((c) => !c.hasAttribute('data-stack-card')).forEach((c) => {
        c.addEventListener('pointermove', (e) => { const r = c.getBoundingClientRect(); gsap.to(c, { rotationY: ((e.clientX - r.left) / r.width - 0.5) * 10, rotationX: -((e.clientY - r.top) / r.height - 0.5) * 10, transformPerspective: 900, duration: 0.5, ease: 'power3' }); });
        c.addEventListener('pointerleave', () => gsap.to(c, { rotationX: 0, rotationY: 0, duration: 0.8, ease: 'elastic.out(1,.5)' }));
      });
    });
    $$('[data-scrub]', root).forEach((el) => {
      if (el._nx) return; el._nx = 1;
      splitWords(el, 'scrub');
      gsap.fromTo($$('.sw', el), { opacity: 0.16 }, { opacity: 1, ease: 'none', stagger: 0.5, duration: 1, scrollTrigger: { trigger: el, start: 'top 82%', end: 'bottom 55%', scrub: 0.4 } });
    });
    $$('[data-parallax]', root).forEach((el) => {
      if (el._nx) return; el._nx = 1;
      const v = +el.dataset.parallax || 0.1;
      gsap.fromTo(el, { yPercent: v * 100 }, { yPercent: -v * 100, ease: 'none', scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } });
    });
  }

  /* ---------- buttons: liquid hover origin + magnetic ---------- */
  document.addEventListener('pointerover', (e) => {
    const b = e.target.closest('.btn'); if (!b || b.contains(e.relatedTarget)) return;
    const r = b.getBoundingClientRect(); b.style.setProperty('--x', e.clientX - r.left + 'px'); b.style.setProperty('--y', e.clientY - r.top + 'px');
  });
  document.addEventListener('pointerout', (e) => {
    const b = e.target.closest('.btn'); if (!b || b.contains(e.relatedTarget)) return;
    const r = b.getBoundingClientRect(); b.style.setProperty('--x', e.clientX - r.left + 'px'); b.style.setProperty('--y', e.clientY - r.top + 'px');
  });
  function initMagnetic(root) {
    if (!fine || !motionOn()) return;
    $$('.magnetic', root).forEach((el) => {
      if (el._mg) return; el._mg = 1;
      const xt = gsap.quickTo(el, 'x', { duration: 0.6, ease: 'power3' }), yt = gsap.quickTo(el, 'y', { duration: 0.6, ease: 'power3' });
      el.addEventListener('pointermove', (e) => { const r = el.getBoundingClientRect(); xt((e.clientX - (r.left + r.width / 2)) * 0.25); yt((e.clientY - (r.top + r.height / 2)) * 0.35); });
      el.addEventListener('pointerleave', () => { xt(0); yt(0); });
    });
  }

  /* ---------- island: a pill that stretches into its own menu (like the Dynamic Island) ----------
     host: the pill's box (skin + sheet are positioned inside it). The skin is one frosted surface whose
     outline is drawn every frame as a path: the leading edge bulges with the speed it moves and the sides
     pinch in while it stretches, so the shape behaves like liquid and settles into a clean rounded panel.
     dir 'up' grows above the pill (sticky bar), 'down' grows below it (header). */
  NX.island = ({ host, sheet, dir = 'up', radius = 28, fade = [], items = () => [], wobble = null, onState }) => {
    const PAD = 28;
    const skin = document.createElement('div');
    skin.className = 'isl-skin'; skin.setAttribute('aria-hidden', 'true');
    skin.innerHTML = '<i class="isl-fill"></i><svg class="isl-rim" preserveAspectRatio="none"><defs><linearGradient id="islG' + (NX._isl = (NX._isl || 0) + 1) + '" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".55"/><stop offset=".45" stop-color="#fff" stop-opacity=".06"/><stop offset="1" stop-color="#fff" stop-opacity=".3"/></linearGradient></defs><path/></svg><i class="isl-shade"></i>';
    host.prepend(skin);
    const fill = $('.isl-fill', skin), rim = $('.isl-rim', skin), edge = $('path', rim), shade = $('.isl-shade', skin);
    edge.setAttribute('stroke', `url(#islG${NX._isl})`);
    let open = false, tl = null, W = 0, H = 0, h0 = 0, BW = 0, BH = 0;
    const s = { y: 0, r: 0, bl: 0, bs: 0 }; // y = moving edge, r = corner radius, bl = leading-edge bulge, bs = side pinch
    let lastY = 0, lastT = 0;
    // rounded rect with a bulge on the moving edge and a pinch on both sides, in skin-box coordinates
    const path = (o = 0) => {
      const L = PAD - o, R = BW - PAD - o;
      const top = (dir === 'up' ? s.y : PAD) - o, bot = (dir === 'up' ? BH - PAD : s.y) - o;
      const w = R - L, h = Math.max(1, bot - top), r = Math.min(s.r, w / 2, h / 2), k = 0.5523 * r;
      const bt = dir === 'up' ? s.bl : 0, bb = dir === 'down' ? s.bl : 0, bs = s.bs, c = 1 / 0.75;
      const ex = (w - 2 * r) / 3, ey = (h - 2 * r) / 3;
      return `M${L + r},${top}C${L + r + ex},${top - bt * c} ${R - r - ex},${top - bt * c} ${R - r},${top}` +
        `C${R - r + k},${top} ${R},${top + r - k} ${R},${top + r}` +
        `C${R + bs * c},${top + r + ey} ${R + bs * c},${bot - r - ey} ${R},${bot - r}` +
        `C${R},${bot - r + k} ${R - r + k},${bot} ${R - r},${bot}` +
        `C${R - r - ex},${bot + bb * c} ${L + r + ex},${bot + bb * c} ${L + r},${bot}` +
        `C${L + r - k},${bot} ${L},${bot - r + k} ${L},${bot - r}` +
        `C${L - bs * c},${bot - r - ey} ${L - bs * c},${top + r + ey} ${L},${top + r}` +
        `C${L},${top + r - k} ${L + r - k},${top} ${L + r},${top}Z`;
    };
    const draw = () => {
      // edge speed (px/s) -> bulge; smoothed so the surface ripples instead of jittering
      const now = performance.now(), dt = Math.max(8, now - lastT) / 1000, v = (lastY - s.y) / dt * (dir === 'up' ? 1 : -1);
      lastY = s.y; lastT = now;
      s.bl += (clamp(v * 0.011, -12, 18) - s.bl) * 0.35;
      s.bs += (-clamp(Math.abs(v) * 0.0035, 0, 6) - s.bs) * 0.3;
      const d = path();
      fill.style.clipPath = `path('${d}')`; edge.setAttribute('d', d);
      if (!host.classList.contains('isl-settled')) sheet.style.clipPath = `path('${path(PAD)}')`;
    };
    const measure = () => {
      W = host.offsetWidth; h0 = host.offsetHeight;
      host.style.setProperty('--isl-h0', h0 + 'px');
      H = Math.max(h0, sheet.offsetHeight);
      BW = W + PAD * 2; BH = H + PAD * 2;
      Object.assign(skin.style, { left: -PAD + 'px', width: BW + 'px', height: BH + 'px', top: dir === 'up' ? (h0 - H - PAD) + 'px' : -PAD + 'px' });
      rim.setAttribute('viewBox', `0 0 ${BW} ${BH}`);
      Object.assign(shade.style, { left: PAD + 'px', right: PAD + 'px', top: PAD + 'px', bottom: PAD + 'px', borderRadius: radius + 'px' });
    };
    const closedY = () => (dir === 'up' ? BH - PAD - h0 : PAD + h0);
    const openY = () => (dir === 'up' ? PAD : BH - PAD);
    const set = (y, r) => { s.y = lastY = y; s.r = r; s.bl = s.bs = 0; lastT = performance.now(); draw(); };

    const toggle = (want) => {
      if (want === open) return Promise.resolve();
      open = want;
      tl && tl.kill();
      const els = items();
      return new Promise((done) => {
        if (want) {
          host.classList.add('isl-on'); sheet.hidden = false; sheet.setAttribute('aria-hidden', 'false');
          measure(); set(closedY(), h0 / 2);
          onState && onState(true);
          if (!motionOn()) { set(openY(), radius); host.classList.add('isl-settled'); sheet.style.clipPath = ''; fade.forEach((f) => (f.style.opacity = 0)); return done(); }
          tl = gsap.timeline({ onUpdate: draw, onComplete: () => { host.classList.add('isl-settled'); sheet.style.clipPath = ''; done(); } });
          tl.to(fade, { opacity: 0, y: dir === 'up' ? -6 : 0, filter: 'blur(6px)', duration: 0.22, ease: 'power2.in' }, 0)
            // a short squash before it lets go, then the stretch with a liquid overshoot
            .to(s, { y: closedY() + (dir === 'up' ? 4 : -4), duration: 0.12, ease: 'power2.out' }, 0)
            .to(s, { y: openY(), duration: 1.05, ease: 'elastic.out(1,0.62)' }, 0.1)
            .to(s, { r: radius, duration: 0.5, ease: 'power3.out' }, 0.1)
            .fromTo(els, { opacity: 0, y: dir === 'up' ? 22 : -14, filter: 'blur(10px)', scale: 0.97 },
              { opacity: 1, y: 0, filter: 'blur(0px)', scale: 1, duration: 0.7, ease: 'expo.out', stagger: 0.045, clearProps: 'filter,transform' }, 0.26);
        } else {
          host.classList.remove('isl-settled'); sheet.setAttribute('aria-hidden', 'true');
          onState && onState(false);
          const end = () => { host.classList.remove('isl-on'); sheet.hidden = true; sheet.style.clipPath = ''; fill.style.clipPath = ''; done(); };
          if (!motionOn()) { fade.forEach((f) => (f.style.opacity = '')); return end(); }
          tl = gsap.timeline({ onUpdate: draw, onComplete: end });
          tl.to(els, { opacity: 0, y: dir === 'up' ? 12 : -8, filter: 'blur(6px)', duration: 0.2, ease: 'power2.in', stagger: { each: 0.015, from: 'end' } }, 0)
            .to(s, { y: closedY(), duration: 0.62, ease: 'expo.inOut' }, 0.08)
            .to(s, { r: h0 / 2, duration: 0.4, ease: 'power2.in' }, 0.3)
            .to(fade, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.4, ease: 'power2.out', clearProps: 'all' }, 0.5)
          // the pill lands and wobbles once, like a drop settling
          if (wobble) tl.fromTo(wobble, { scaleY: 1, scaleX: 1 }, { keyframes: [{ scaleY: 1.07, scaleX: 0.985, duration: 0.12, ease: 'power2.out' }, { scaleY: 1, scaleX: 1, duration: 0.55, ease: 'elastic.out(1,0.45)' }], transformOrigin: dir === 'up' ? '50% 100%' : '50% 0%', clearProps: 'transform' }, 0.66);
        }
      });
    };
    addEventListener('resize', () => { if (open) { measure(); set(openY(), radius); } });
    return { open: () => toggle(true), close: () => toggle(false), isOpen: () => open, refresh: () => { if (open && host.classList.contains('isl-settled')) { measure(); set(openY(), radius); } } };
  };

  /* ---------- header + menu ---------- */  /* ---------- header + menu ---------- */
  function closeMenu() { if (!html.classList.contains('menu-open')) return; html.classList.remove('menu-open'); const b = $('[data-burger]'); b && b.setAttribute('aria-expanded', 'false'); NX.lock(false); }
  NX.modules.header = (el) => {
    const ind = $('.nav-ind', el), links = $$('.nav-links a', el);
    links.forEach((a) => a.addEventListener('pointerenter', () => { ind.style.width = a.offsetWidth + 'px'; ind.style.transform = `translateX(${a.offsetLeft}px)`; ind.style.opacity = 1; }));
    const nl = $('.nav-links', el); nl && nl.addEventListener('pointerleave', () => (ind.style.opacity = 0));
    const burger = $('[data-burger]', el), menu = $('.menu', el);
    const sheet = $('[data-nav-sheet]', el), navEl = $('.nav', el);
    const usePop = () => sheet && el.dataset.mobileMenu !== 'fullscreen' && matchMedia('(max-width: 989px)').matches;
    let popOpen = false;
    const isl = sheet && NX.island({ host: navEl, sheet, dir: 'down', radius: 26, items: () => $$('.np-link, .np-shop, .np-sub, .np-note, .np-quiz', sheet) });
    const openPop = () => {
      if (popOpen) return; popOpen = true;
      html.classList.add('pop-open'); burger.setAttribute('aria-expanded', 'true');
      NX.scrim(true, 93, closePop); NX.lock(true);
      isl.open();
    };
    const closePop = () => {
      if (!popOpen) return; popOpen = false;
      html.classList.remove('pop-open'); burger.setAttribute('aria-expanded', 'false');
      NX.scrim(false); if (!document.querySelector('#CartDrawer.open')) NX.lock(false);
      return isl.close();
    };
    if (sheet) {
      $$('a', sheet).forEach((a) => a.addEventListener('click', closePop));
      const qz = $('[data-nav-quiz]', sheet);
      qz && qz.addEventListener('click', async () => { await closePop(); NX.openNight && NX.openNight(navEl); });
      document.addEventListener('pointerdown', (e) => { if (popOpen && !navEl.contains(e.target)) closePop(); });
      addEventListener('keydown', (e) => e.key === 'Escape' && closePop());
      addEventListener('resize', () => popOpen && !usePop() && closePop());
    }
    burger && burger.addEventListener('click', () => {
      if (usePop()) { popOpen ? closePop() : openPop(); return; }
      const r = burger.getBoundingClientRect();
      menu.style.setProperty('--mx', r.left + r.width / 2 + 'px'); menu.style.setProperty('--my', r.top + r.height / 2 + 'px');
      const open = html.classList.toggle('menu-open');
      burger.setAttribute('aria-expanded', open); NX.lock(open);
    });
    $$('a', menu || el).forEach((a) => a.addEventListener('click', closeMenu));
    addEventListener('keydown', (e) => e.key === 'Escape' && closeMenu());
    if (el.dataset.hide === 'true') {
      let last = scrollY;
      addEventListener('scroll', () => { const nav = $('.nav', el); const y = scrollY; nav.classList.toggle('is-hidden', y > 300 && y > last && !html.classList.contains('menu-open')); last = y; }, { passive: true });
    }
  };

  /* ---------- cart ---------- */
  // visible viewport height (excludes mobile browser toolbars) so the cart sheet always fits on screen
  const setVVH = () => html.style.setProperty('--vvh', Math.round(window.visualViewport ? visualViewport.height : innerHeight) + 'px');
  setVVH(); addEventListener('resize', setVVH);
  window.visualViewport && visualViewport.addEventListener('resize', setVVH);
  const Cart = (NX.cart = {});
  const drawer = () => $('#CartDrawer');
  const sectionsWanted = () => {
    const ids = ['cart-drawer'];
    $$('[data-cart-section]').forEach((e) => ids.push(e.dataset.cartSection));
    return [...new Set(ids)];
  };
  let own = 0; // our own cart requests are not re-handled by the fetch watcher
  async function cartFetch(url, body) {
    own++;
    try {
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ ...body, sections: sectionsWanted(), sections_url: location.pathname }) });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Something went wrong');
      return data;
    } finally { own--; }
  }
  function renderSections(sections) {
    if (!sections) return;
    for (const [id, htmlStr] of Object.entries(sections)) {
      if (!htmlStr) continue;
      const doc = new DOMParser().parseFromString(htmlStr, 'text/html');
      if (id === 'cart-drawer') {
        const inner = $('#CartDrawerInner', doc), cur = $('#CartDrawerInner');
        if (inner && cur) { cur.replaceWith(inner); init(inner); }
      } else {
        const host = $(`[data-cart-section="${id}"]`), fresh = $(`[data-cart-section="${id}"]`, doc);
        if (host && fresh) { host.replaceWith(fresh); init(fresh); }
      }
    }
    const c = $('#CartDrawerInner');
    if (c) $$('[data-cart-count]').forEach((b) => { b.textContent = c.dataset.items; b.dataset.n = c.dataset.items; });
  }
  Cart.refresh = async () => {
    const ids = sectionsWanted().join(',');
    own++;
    try { const r = await fetch(`${location.pathname}?sections=${ids}`); renderSections(await r.json()); } catch (e) { /* ignore */ } finally { own--; }
  };
  Cart.open = () => {
    if (T.cartType === 'page') { location.href = T.routes.cart; return; }
    const d = drawer(); if (!d) return;
    d.classList.add('open'); d.setAttribute('aria-hidden', 'false'); NX.lock(true);
    setTimeout(() => { const x = $('.cd-x', d); x && x.focus({ preventScroll: true }); }, 400);
  };
  Cart.close = () => { const d = drawer(); if (!d) return; d.classList.remove('open'); d.setAttribute('aria-hidden', 'true'); NX.lock(false); };
  Cart.add = async (items, source) => {
    const data = await cartFetch(T.routes.cartAdd + '.js', { items });
    renderSections(data.sections);
    fly(source);
    document.dispatchEvent(new CustomEvent('nx:cart:added', { detail: data }));
    setTimeout(Cart.open, source && T.cartType !== 'page' ? 650 : 0);
    return data;
  };
  Cart.change = async (line, quantity) => {
    const data = await cartFetch(T.routes.cartChange + '.js', { line, quantity });
    renderSections(data.sections);
    return data;
  };
  Cart.update = async (updates, attributes) => {
    const data = await cartFetch(T.routes.cartUpdate + '.js', { updates, attributes });
    renderSections(data.sections);
    return data;
  };
  function fly(from) {
    const target = $('[data-cart-icon]');
    if (!from || !target || !motionOn() || T.cartFly === false) return;
    const a = from.getBoundingClientRect(), b = target.getBoundingClientRect();
    if (!a.width || !b.width) return;
    for (let i = 0; i < 4; i++) {
      const c = document.createElement('i'); c.className = 'fly'; document.body.appendChild(c);
      const sx = a.left + a.width / 2 + gsap.utils.random(-30, 30), sy = a.top + a.height / 2;
      gsap.set(c, { x: sx, y: sy, rotate: gsap.utils.random(-40, 40), scale: 0.4 });
      gsap.timeline({ delay: i * 0.06, onComplete: () => { c.remove(); if (i === 3) gsap.fromTo(target, { scale: 1 }, { scale: 1.18, duration: 0.18, yoyo: true, repeat: 1 }); } })
        .to(c, { scale: 1, duration: 0.25, ease: 'back.out(2)' }, 0)
        .to(c, { x: b.left + b.width / 2 - 23, duration: 0.8, ease: 'power2.inOut' }, 0)
        .to(c, { y: sy - 90 - i * 14, duration: 0.36, ease: 'power2.out' }, 0)
        .to(c, { y: b.top + b.height / 2 - 9, duration: 0.46, ease: 'power2.in' }, 0.36)
        .to(c, { rotate: '+=360', scale: 0.5, duration: 0.8, ease: 'none' }, 0);
    }
  }
  // any add-to-cart form on the page goes through the drawer (apps such as Moon Bundles keep working:
  // if an app handles the submit itself and prevents default, we leave it alone and refresh afterwards)
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || e.defaultPrevented) return;
    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add/.test(action) || form.hasAttribute('data-no-ajax') || T.cartType === 'page') return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]') || e.submitter;
    const fd = new FormData(form);
    const item = { id: fd.get('id'), quantity: +(fd.get('quantity') || 1) };
    const props = {}; for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v !== '') props[m[1]] = v; }
    if (Object.keys(props).length) item.properties = props;
    if (fd.get('selling_plan')) item.selling_plan = fd.get('selling_plan');
    const items = [item];
    $$('[data-addon-checked="true"]', form.closest('[data-product-root]') || form).forEach((a) => items.push({ id: a.dataset.variant, quantity: 1 }));
    btn && btn.classList.add('is-loading');
    try { await Cart.add(items, btn); }
    catch (err) { NX.toast(err.message); }
    finally { btn && btn.classList.remove('is-loading'); }
  });
  // watch cart requests made by apps (Moon Bundles, upsell apps) and keep the drawer in sync
  const _fetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const p = _fetch.apply(this, arguments);
    if (!own && url && /\/cart\/(add|change|update|clear)(\.js)?/.test(url) && (!init || (init.method || 'GET').toUpperCase() === 'POST')) {
      p.then((r) => { if (r.ok) setTimeout(() => Cart.refresh().then(() => /add/.test(url) && Cart.open()), 120); }).catch(() => {});
    }
    return p;
  };
  const _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (m, url) {
    if (!own && /\/cart\/(add|change|update|clear)/.test(url) && /post/i.test(m)) this.addEventListener('load', () => { if (this.status < 400) Cart.refresh().then(() => /add/.test(url) && Cart.open()); });
    return _open.apply(this, arguments);
  };
  document.addEventListener('cart:refresh', () => Cart.refresh());
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-cart]')) {
      if (T.cartType === 'page' || !drawer()) return;
      e.preventDefault(); Cart.open();
    }
    if (e.target.closest('[data-close-cart]')) { e.preventDefault(); Cart.close(); }
  });
  addEventListener('keydown', (e) => e.key === 'Escape' && Cart.close());
  NX.modules['cart-ui'] = (el) => {
    el.addEventListener('click', async (e) => {
      const q = e.target.closest('[data-line-qty]');
      if (q) {
        e.preventDefault();
        const line = +q.dataset.line, to = Math.max(0, +q.dataset.lineQty);
        el.classList.add('is-busy');
        try { await Cart.change(line, to); } catch (err) { NX.toast(err.message); } finally { el.classList.remove('is-busy'); }
      }
      const sp = e.target.closest('[data-sp-toggle]');
      if (sp) {
        e.preventDefault();
        const on = sp.getAttribute('aria-checked') === 'true';
        try { on ? await Cart.change(+sp.dataset.line, 0) : await Cart.add([{ id: sp.dataset.variant, quantity: 1 }]); } catch (err) { NX.toast(err.message); }
      }
    });
    el.addEventListener('change', async (e) => {
      const i = e.target.closest('input[data-line]');
      if (!i) return;
      try { await Cart.change(+i.dataset.line, Math.max(0, +i.value)); } catch (err) { NX.toast(err.message); }
    });
    const code = $('[data-discount-form]', el);
    code && code.addEventListener('submit', async (e) => {
      e.preventDefault();
      const v = $('input', code).value.trim(); if (!v) return;
      const msg = $('[data-discount-msg]', el);
      try { await fetch('/discount/' + encodeURIComponent(v)); await Cart.refresh(); msg && (msg.textContent = code.dataset.ok || 'Code applied at checkout.'); }
      catch (err) { msg && (msg.textContent = code.dataset.err || 'That code could not be applied.'); }
    });
    $$('[data-cd-timer]', el).forEach(startEvergreenTimer);
  };
  function startEvergreenTimer(t) {
    if (t._nx) return; t._nx = 1;
    const mins = +t.dataset.cdTimer || 10, key = 'nx-cart-timer', loop = t.dataset.loop === 'true';
    let end = store.get(key);
    if (!end || (end < Date.now() && loop) || end - Date.now() > mins * 60000) { end = Date.now() + mins * 60000; store.set(key, end); }
    const out = $('b', t) || t;
    const tick = () => {
      if (!t.isConnected) return; // drawer re-rendered: the new timer element takes over
      let s = Math.max(0, Math.round((end - Date.now()) / 1000));
      if (s === 0 && loop) { end = Date.now() + mins * 60000; store.set(key, end); s = mins * 60; }
      out.textContent = `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
      t.classList.toggle('is-urgent', s > 0 && s <= 60);
      if (s === 0) { const tx = $('.cd-timer-t', t); if (tx && t.dataset.expired) tx.textContent = t.dataset.expired; return; }
      setTimeout(tick, 1000);
    };
    tick();
  }

  /* ---------- product ---------- */
  /* ---------- the offer (Moon Bundles): read what the widget will put in the cart ----------
     Quantity, one-time or subscription, and add-ons all live in the widget. The theme only reads it,
     so every price it shows (button, title price, sticky bar) equals what checkout charges. */
  const cents = (s) => {
    let t = (String(s || '').match(/\d[\d.,]*/) || [''])[0];
    if (!t) return null;
    t = /[.,]\d{2}$/.test(t) ? t.slice(0, -3).replace(/[.,]/g, '') + '.' + t.slice(-2) : t.replace(/[.,]/g, '');
    return Math.round(parseFloat(t) * 100);
  };
  const offerFns = [];
  let offerEl = null;
  NX.offer = {
    read() {
      const mb = $('moonbundle-component'); if (!mb) return null;
      const opts = $$('.moonbundle-option', mb), active = $('.moonbundle-option.moonbundle-option-active', mb) || opts[0];
      if (!active) return null;
      const plans = $$('button.moonbundle-subscription-radio-option', mb);
      const subBtn = plans.find((b) => $('.moonbundle-subscription-savings-badge', b)) || plans[0] || null;
      const oneBtn = plans.find((b) => b !== subBtn) || null;
      const text = (el) => ((el && el.textContent) || '').replace(/\s+/g, ' ').trim();
      const ups = $$('.moonbundle-upsell-item', mb).map((u) => ({ input: $('input[type="checkbox"]', u), title: text($('.moonbundle-upsell-title', u)), note: text($('.moonbundle-upsell-content p', u)), price: cents(text($('.moonbundle-upsell-price', u))), img: ($('img', u) || {}).src || '' }));
      const price = cents(text($('.moonbundle-option-single-price', active))), compare = cents(text($('.moonbundle-option-old-price', active)));
      const extra = ups.reduce((n, u) => n + (u.input && u.input.checked ? u.price || 0 : 0), 0);
      const subTitle = subBtn && $('.moonbundle-subscription-title', subBtn);
      return {
        mb, opts, index: opts.indexOf(active), sub: !!(subBtn && subBtn.classList.contains('active')), subBtn, oneBtn,
        subLabel: subTitle ? text(subTitle.firstChild) || text(subTitle).replace(text($('.moonbundle-subscription-savings-badge', subTitle)), '').trim() : '',
        subBadge: text(subBtn && $('.moonbundle-subscription-savings-badge', subBtn)), subNote: text(subBtn && $('.moonbundle-subscription-subtitle', subBtn)),
        oneLabel: text(oneBtn && $('.moonbundle-subscription-title', oneBtn)) || text(oneBtn && $('.moonbundle-subscription-option-title', oneBtn)),
        ups, price, compare, total: price == null ? null : price + extra, compareTotal: compare == null ? null : compare + extra,
      };
    },
    // run fn now and whenever the widget's selection changes
    watch(fn) {
      offerFns.push(fn);
      let t = 0;
      const run = () => { clearTimeout(t); t = setTimeout(() => { const s = NX.offer.read(); if (s) offerFns.forEach((f) => f(s)); }, 60); };
      if (NX.offer._run) { const s = NX.offer.read(); if (s) fn(s); return; }
      NX.offer._run = run;
      const attach = () => {
        const mb = $('moonbundle-component'); if (!mb || mb === offerEl) return !!mb;
        offerEl = mb;
        new MutationObserver(run).observe(mb, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'checked', 'aria-checked'] });
        mb.addEventListener('change', run); mb.addEventListener('click', run);
        run(); return true;
      };
      if (!attach()) { let n = 0; const iv = setInterval(() => { if (attach() || ++n > 60) clearInterval(iv); }, 500); }
    },
  };
  /* the widget's one-time / subscribe choice as one pill (like the sticky bar); while subscribing,
     the renewal is spelled out right under it, so the compact pill never hides that it renews */
  function compactPlans(s) {
    const box = s.subBtn && s.subBtn.closest('.moonbundle-subscription-radios');
    if (!box || !s.oneBtn) return;
    if (!box.classList.contains('nx-plan-pill')) {
      box.classList.add('nx-plan-pill');
      // the track takes the colors of an unselected quantity row, so Moon Bundles' design settings still apply
      const offEl = s.opts.find((o) => !o.classList.contains('moonbundle-option-active')) || (s.sub ? s.oneBtn : s.subBtn), off = getComputedStyle(offEl);
      box.style.setProperty('--nxp-bg', off.backgroundColor);
      box.style.setProperty('--nxp-line', off.borderTopColor);
    }
    const toggle = box.closest('.moonbundle-subscription-toggle') || box;
    let note = toggle.nextElementSibling && toggle.nextElementSibling.classList.contains('nx-plan-note') ? toggle.nextElementSibling : null;
    if (!note) { note = document.createElement('p'); note.className = 'nx-plan-note'; toggle.after(note); }
    const renew = ((T.offerRenew || [])[s.index] || '').replace(/\.$/, '');
    note.textContent = s.sub ? [renew, s.subNote].filter(Boolean).join('. ') : '';
    note.hidden = !s.sub;
  }
  NX.modules.product = (root) => {
    const form = $('form[action*="/cart/add"]', root);
    const idInput = form && $('input[name="id"]', form);
    const dataEl = $('script[data-product-json]', root);
    const product = dataEl ? JSON.parse(dataEl.textContent) : null;
    const priceEls = $$('[data-price-target]', root);
    const btns = $$('[data-add-btn]', root);
    function currentOptions() {
      return $$('[data-option-index]', root).map((g) => { const c = $('input:checked', g) || $('select', g); return c ? c.value : null; });
    }
    function update() {
      if (!product || !Array.isArray(product.variants)) return;
      const opts = currentOptions();
      const v = product.variants.find((x) => x.options.every((o, i) => opts[i] == null || o === opts[i]));
      if (!v) return;
      idInput && (idInput.value = v.id);
      const q = +(($('input[name="quantity"]', form) || {}).value || 1);
      const mult = root.dataset.multiply === 'true' ? q : 1;
      priceEls.forEach((p) => { p.innerHTML = `<b>${money(v.price * mult)}</b>${v.compare_at_price > v.price ? `<s>${money(v.compare_at_price * mult)}</s>` : ''}`; });
      btns.forEach((b) => { b.disabled = !v.available; const l = $('[data-add-label]', b); if (l) l.textContent = v.available ? (l.dataset.label || 'Add to cart') : 'Sold out'; });
      if (v.featured_media) document.dispatchEvent(new CustomEvent('nx:variant-media', { detail: { id: v.featured_media.id, root } }));
      if (root.dataset.updateUrl !== 'false' && !T.designMode) history.replaceState(null, '', `${location.pathname}?variant=${v.id}`);
      root.dispatchEvent(new CustomEvent('nx:variant', { detail: v, bubbles: true }));
    }
    root.addEventListener('change', (e) => { if (e.target.closest('[data-option-index]')) update(); });
    $$('[data-qty]', root).forEach((q) => {
      const input = $('input', q);
      $$('button', q).forEach((b) => b.addEventListener('click', () => { input.value = Math.max(1, +input.value + (+b.dataset.step)); input.dispatchEvent(new Event('change', { bubbles: true })); update(); }));
    });
    $$('[data-addon]', root).forEach((a) => a.addEventListener('click', () => { const on = a.dataset.addonChecked !== 'true'; a.dataset.addonChecked = on; a.setAttribute('aria-checked', on); }));
    $$('[data-ship-date]', root).forEach(shipDate);
    // with a bundle widget on the page, the prices follow its selection (quantity, subscription, add-ons)
    NX.offer.watch((s) => {
      compactPlans(s);
      if (s.total == null) return;
      priceEls.forEach((p) => {
        if (p.classList.contains('pd-atc-price')) p.textContent = '· ' + money(s.total);
        else p.innerHTML = `<b>${money(s.total)}</b>${s.compareTotal > s.total ? `<s>${money(s.compareTotal)}</s>` : ''}`;
      });
    });
  };
  function shipDate(el) {
    const d = new Date(Date.now() + (+el.dataset.shipDate || 1) * 864e5);
    el.textContent = d.toLocaleDateString('en-US', el.dataset.format === 'weekday' ? { weekday: 'long', month: 'long', day: 'numeric' } : { month: 'long', day: 'numeric' });
  }
  NX.modules['ship-date'] = shipDate;

  NX.modules.gallery = (root) => {
    const main = $('[data-gallery-main]', root); if (!main) return;
    const slides = $$('[data-slide]', main), thumbs = $$('[data-thumb]', root), dots = $$('[data-dot]', root), now = $('[data-now]', root);
    let cur = Math.max(0, slides.findIndex((s) => s.classList.contains('on'))), zoom = false;
    const go = (i, x = 50, y = 50) => {
      i = (i + slides.length) % slides.length; if (i === cur) return;
      main.classList.remove('zoom'); zoom = false;
      const prev = slides[cur]; prev.classList.remove('on'); prev.classList.add('was');
      slides[i].style.setProperty('--cx', x + '%'); slides[i].style.setProperty('--cy', y + '%');
      void main.offsetWidth; slides[i].classList.add('on');
      setTimeout(() => prev.classList.remove('was'), 1100);
      thumbs.forEach((t, k) => t.classList.toggle('on', k === i)); dots.forEach((d, k) => d.classList.toggle('on', k === i));
      now && (now.textContent = i + 1);
      $$('video', prev).forEach((v) => v.pause()); $$('video', slides[i]).forEach((v) => { const p = v.play(); p && p.catch(() => {}); });
      cur = i;
      const t = thumbs[i]; if (t && t.parentElement.scrollWidth > t.parentElement.clientWidth) t.parentElement.scrollTo({ left: t.offsetLeft - t.parentElement.clientWidth / 2 + t.offsetWidth / 2, behavior: 'smooth' });
    };
    thumbs.forEach((t, i) => t.addEventListener('click', () => {
      const r = t.getBoundingClientRect(), m = main.getBoundingClientRect();
      go(i, clamp(((r.left + r.width / 2 - m.left) / m.width) * 100, 0, 100), clamp(((r.top + r.height / 2 - m.top) / m.height) * 100, 0, 100));
    }));
    $$('[data-prev]', root).forEach((b) => b.addEventListener('click', () => go(cur - 1, 0, 50)));
    $$('[data-next]', root).forEach((b) => b.addEventListener('click', () => go(cur + 1, 100, 50)));
    let sx = null, sy = 0, moved = false;
    main.addEventListener('pointerdown', (e) => { sx = e.clientX; sy = e.clientY; moved = false; });
    main.addEventListener('pointermove', (e) => {
      if (zoom && fine) { const r = main.getBoundingClientRect(); const img = $('img', slides[cur]); img && (img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`); }
      if (sx !== null && Math.abs(e.clientX - sx) > 8) moved = true;
    });
    main.addEventListener('pointerup', (e) => {
      if (sx === null) return;
      const dx = e.clientX - sx, dy = e.clientY - sy; sx = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) return go(cur + (dx < 0 ? 1 : -1), dx < 0 ? 100 : 0, 50);
      if (!moved && fine && main.dataset.zoom !== 'false' && !$('video', slides[cur])) { zoom = !zoom; main.classList.toggle('zoom', zoom); }
    });
    main.addEventListener('pointerleave', () => (sx = null));
    document.addEventListener('nx:variant-media', (e) => { const i = slides.findIndex((s) => s.dataset.mediaId == e.detail.id); if (i > -1) go(i); });
  };

  NX.modules['sticky-atc'] = (el) => {
    const mode = el.dataset.trigger || 'button';
    const watch = $(el.dataset.watch || '[data-add-btn]');
    let past = false, inFooter = false;
    let closeQ = () => {};
    // theme editor: keep the bar on screen while its section is selected or was just re-rendered,
    // so changes (color preset, glass) are visible without scrolling past the product's button
    let forced = !!T.designMode && document.readyState === 'complete';
    if (T.designMode) {
      document.addEventListener('shopify:section:select', (e) => { if (e.target.contains(el)) { forced = true; upd(); } });
      document.addEventListener('shopify:section:deselect', (e) => { if (e.target.contains(el)) { forced = false; upd(); } });
    }
    const upd = () => { const on = (past && !inFooter) || forced; el.classList.toggle('show', on); el.setAttribute('aria-hidden', !on); if (!on) closeQ(); };
    if (mode === 'scroll_percentage') {
      const pct = +el.dataset.pct || 15;
      addEventListener('scroll', () => { past = scrollY / Math.max(1, document.body.scrollHeight - innerHeight) * 100 > pct; upd(); }, { passive: true });
    } else if (watch) {
      new IntersectionObserver(([e]) => { past = !e.isIntersecting && e.boundingClientRect.top < 0; upd(); }).observe(watch);
    } else { past = true; }
    const foot = $('.site-footer'); foot && new IntersectionObserver(([e]) => { inFooter = e.isIntersecting; upd(); }, { threshold: 0.05 }).observe(foot);

    const bundleEl = () => $('moonbundle-component') || $('[data-product-root] form') || $('[data-product-root]');
    const options = () => $$('moonbundle-component .moonbundle-option');
    const realAdd = () => $('[data-product-root] [data-add-btn]') || $('[data-add-btn]');
    const goToBundles = () => {
      const t = bundleEl(); if (!t) return false;
      const y = t.getBoundingClientRect().top + scrollY - Math.max(90, (innerHeight - t.offsetHeight) / 2);
      if (NX.lenis) NX.lenis.scrollTo(Math.max(0, y), { duration: 1.4 }); else scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      setTimeout(() => { t.classList.remove('bundle-flash'); void t.offsetWidth; t.classList.add('bundle-flash'); }, 900);
      return true;
    };
    // arriving from another page's sticky bar (advertorials link here with #bundles)
    if (location.hash === '#bundles') setTimeout(goToBundles, 900);

    const btn = $('[data-satc-btn]', el);
    btn && btn.addEventListener('click', (e) => {
      const behavior = el.dataset.behavior;
      if (behavior === 'bundle') {
        if (goToBundles()) { e.preventDefault(); return; }
        const u = new URL(btn.href, location.href);
        if (!u.hash) { e.preventDefault(); u.hash = 'bundles'; location.href = u.toString(); }
        return;
      }
      if (behavior === 'add') {
        const f = $('form.pd-form') || $('form[action*="/cart/add"]');
        if (f) { e.preventDefault(); f.requestSubmit ? f.requestSubmit() : f.submit(); return; }
      }
      if (behavior === 'scroll') { const t = $('[data-product-root]') || $('main'); if (t) { e.preventDefault(); NX.scrollTo(t); } }
    });
    $$('[data-ship-date]', el).forEach(shipDate);

    /* quick pick: a liquid menu that mirrors the Moon Bundles options and drives the real widget */
    const q = $('[data-satc-q]', el), pop = $('[data-satc-sheet]', el), pill = $('.satc-pill', el);
    if (!q || !pop) return;
    let qOpen = false, pick = 0;
    const showQ = () => { q.hidden = options().length < 1; };
    showQ(); new MutationObserver(showQ).observe(document.body, { childList: true, subtree: true });
    const money = (s) => (String(s).match(/[^\s|]*\d[\d.,]*/) || [''])[0];
    const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const box = () => $('[data-satc-opts]', pop);
    // one option row, read from the widget: title, label, best-seller badge, image, prices
    const rowData = (o) => {
      const title = (($('.moonbundle-option-title', o) || {}).textContent || '').trim();
      const lbl = (($('.moonbundle-label-inside', o) || {}).textContent || '').trim();
      const badge = (((o.parentElement && $('.moonbundle-best-seller-box', o.parentElement)) || {}).textContent || '').trim();
      const img = ($('img', o) || {}).src || '';
      const lines = o.innerText.split('\n').map((x) => x.trim()).filter(Boolean);
      const prices = lines.filter((x) => /\d/.test(x) && /[$€£]|\d[.,]\d\d/.test(x) && !/per|save|%/i.test(x));
      const sub = lines.find((x) => x !== title && x !== lbl && x !== badge && !prices.includes(x) && !/per /i.test(x) && x.length < 30) || '';
      const pp = '<b>' + esc(money(prices[0] || '')) + '</b>' + (prices.length > 1 && prices[prices.length - 1] !== prices[0] ? '<s>' + esc(money(prices[prices.length - 1])) + '</s>' : '');
      return { title, lbl, badge, img, sub, pp };
    };
    // a value that changes rolls in softly instead of jumping
    const swapText = (node, html, isText) => {
      if ((isText ? node.textContent : node.innerHTML) === html) return;
      if (isText) node.textContent = html; else node.innerHTML = html;
      if (motionOn() && node.animate) node.animate([{ opacity: 0, transform: 'translateY(6px)', filter: 'blur(4px)' }, { opacity: 1, transform: 'none', filter: 'blur(0)' }], { duration: 420, easing: 'cubic-bezier(.22,1,.36,1)' });
    };
    // full render: when the menu opens (its items then rise in with the island)
    const build = () => {
      const bx = box(); bx.innerHTML = '';
      const opts = options();
      pick = Math.max(0, opts.findIndex((o) => o.classList.contains('moonbundle-option-active')));
      opts.forEach((o, i) => {
        const d = rowData(o);
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'satc-opt'; b.setAttribute('role', 'radio'); b.setAttribute('aria-checked', i === pick);
        b.innerHTML = (d.badge ? '<span class="bdg">' + esc(d.badge) + '</span>' : '') + '<span class="rd"></span>' + (d.img ? '<img src="' + esc(d.img) + '" alt="">' : '<span></span>') +
          '<span class="tt"><b>' + esc(d.title) + '</b>' + (d.lbl ? '<span class="lbl">' + esc(d.lbl) + '</span>' : '') + (d.sub ? '<small>' + esc(d.sub) + '</small>' : '') + '</span>' +
          '<span class="pp">' + d.pp + '</span>';
        b.addEventListener('click', () => { pick = i; $$('.satc-opt', bx).forEach((x, k) => x.setAttribute('aria-checked', k === i)); if (!o.classList.contains('moonbundle-option-active')) { o.click(); later(); } });
        bx.appendChild(b);
      });
      buildExtra(true);
    };
    // after a tap: change only what changed, so the transitions play instead of a re-render
    const sync = () => {
      const opts = options(), rows = $$('.satc-opt', box());
      if (rows.length !== opts.length) { build(); isl.refresh(); return; }
      pick = Math.max(0, opts.findIndex((o) => o.classList.contains('moonbundle-option-active')));
      opts.forEach((o, i) => { rows[i].setAttribute('aria-checked', i === pick); swapText($('.pp', rows[i]), rowData(o).pp); });
      if (buildExtra(false)) isl.refresh();
    };
    // one-time / subscription and add-ons, mirrored from the widget; every tap drives the real widget
    const extra = $('[data-satc-extra]', pop), totalEl = $('[data-satc-total]', pop);
    const renew = (el.dataset.renew || '').split('|'), oneNote = el.dataset.onetime || '';
    let lt = 0;
    const later = () => { clearTimeout(lt); lt = setTimeout(() => { if (qOpen) sync(); }, 120); };
    // returns true when the layout changed (rows added or removed)
    const buildExtra = (full) => {
      const s = NX.offer.read();
      if (totalEl) swapText(totalEl, s && s.total != null ? '· ' + NX.money(s.total) : '', true);
      if (!extra) return false;
      if (!s) { const had = !!extra.innerHTML; extra.innerHTML = ''; return had; }
      const hasPlan = !!(s.subBtn && s.oneBtn), ups = s.ups.map((u, i) => [u, i]).filter(([u]) => u.input);
      const note = s.sub ? [renew[s.index] || '', s.subNote].filter(Boolean).join('. ').replace(/\.\./g, '.') : oneNote;
      let changed = false;
      if (full || !!$('.satc-plan', extra) !== hasPlan || $$('.satc-up', extra).length !== ups.length) {
        changed = true;
        let h = '';
        if (hasPlan) {
          h += '<div class="satc-plan" role="radiogroup" aria-label="Purchase type" data-sub="' + s.sub + '">' +
            '<button type="button" class="satc-seg" role="radio" data-plan="one" aria-checked="' + !s.sub + '">' + esc(s.oneLabel || 'One-time purchase') + '</button>' +
            '<button type="button" class="satc-seg" role="radio" data-plan="sub" aria-checked="' + s.sub + '">' + esc(s.subLabel || 'Subscribe & Save') + (s.subBadge ? ' <span>' + esc(s.subBadge) + '</span>' : '') + '</button></div>' +
            '<p class="satc-renew" aria-live="polite">' + esc(note) + '</p>';
        }
        ups.forEach(([u, i]) => {
          h += '<button type="button" class="satc-up" role="switch" data-up="' + i + '" aria-checked="' + u.input.checked + '">' + (u.img ? '<img src="' + esc(u.img) + '" alt="">' : '<span></span>') +
            '<span class="tt"><b>' + esc(u.title) + '</b>' + (u.note ? '<small>' + esc(u.note) + '</small>' : '') + '</span><span class="pp">+' + esc(u.price != null ? NX.money(u.price) : '') + '</span><span class="sw" aria-hidden="true"></span></button>';
        });
        extra.innerHTML = h;
        $$('[data-plan]', extra).forEach((b) => b.addEventListener('click', () => {
          const s2 = NX.offer.read(); if (!s2) return; const want = b.dataset.plan === 'sub';
          if (want === s2.sub) return;
          // the thumb slides right away; the widget catches up underneath
          const pl = $('.satc-plan', extra); pl.dataset.sub = want; $$('.satc-seg', pl).forEach((x) => x.setAttribute('aria-checked', String((x.dataset.plan === 'sub') === want)));
          (want ? s2.subBtn : s2.oneBtn).click(); later();
        }));
        $$('[data-up]', extra).forEach((b) => b.addEventListener('click', () => {
          const s2 = NX.offer.read(); const u = s2 && s2.ups[+b.dataset.up];
          if (u && u.input) { b.setAttribute('aria-checked', String(!u.input.checked)); u.input.click(); later(); }
        }));
        return changed;
      }
      const pl = $('.satc-plan', extra);
      if (pl) {
        pl.dataset.sub = s.sub;
        $$('.satc-seg', pl).forEach((x) => x.setAttribute('aria-checked', String((x.dataset.plan === 'sub') === s.sub)));
        const rn = $('.satc-renew', extra); rn && swapText(rn, note, true);
      }
      $$('.satc-up', extra).forEach((b) => { const u = s.ups[+b.dataset.up]; if (u && u.input) b.setAttribute('aria-checked', String(u.input.checked)); });
      return false;
    };
    const satcPrice = $('.satc-p', el);
    if (satcPrice) NX.offer.watch((s) => { if (s.total != null) swapText(satcPrice, `<span class="price"><b>${NX.money(s.total)}</b>${s.compareTotal > s.total ? `<s>${NX.money(s.compareTotal)}</s>` : ''}</span>`); if (qOpen) buildExtra(false); });
    const isl = NX.island({ host: el, sheet: pop, dir: 'up', radius: 28, fade: $$(':scope > *', pill), wobble: pill, items: () => $$('.satc-pop-head, .satc-opt, .satc-plan, .satc-renew, .satc-up, .satc-quiz, .satc-add', pop) });
    const toggleQ = (open) => {
      if (open === qOpen) return Promise.resolve(); qOpen = open;
      q.setAttribute('aria-expanded', open);
      if (open) { build(); NX.scrim(true, 140, () => toggleQ(false)); NX.lock(true); }
      else { NX.scrim(false); if (!document.querySelector('#CartDrawer.open')) NX.lock(false); }
      return open ? isl.open() : isl.close();
    };
    closeQ = () => { if (qOpen) toggleQ(false); };
    q.addEventListener('click', () => toggleQ(!qOpen));
    $('[data-satc-close]', pop).addEventListener('click', () => toggleQ(false));
    document.addEventListener('pointerdown', (e) => { if (qOpen && !el.contains(e.target)) toggleQ(false); });
    addEventListener('keydown', (e) => e.key === 'Escape' && toggleQ(false));
    // "not sure which?" -> the bar folds back and the 3AM quiz opens from it
    const quiz = $('[data-satc-quiz]', pop);
    quiz && quiz.addEventListener('click', async () => { await toggleQ(false); NX.openNight && NX.openNight(pill); });
    const add = $('[data-satc-add]', pop);
    add.addEventListener('click', () => {
      const o = options()[pick], real = realAdd();
      if (!o || !real) { toggleQ(false); goToBundles(); return; }
      add.classList.add('is-loading');
      if (!o.classList.contains('moonbundle-option-active')) o.click(); // select the option in the real Moon Bundles widget (plan + add-ons are already set there), then press the real button so the bundle discount applies
      setTimeout(() => { real.click(); setTimeout(() => { add.classList.remove('is-loading'); toggleQ(false); }, 700); }, 250);
    });
  };

  /* ---------- hero (phones): the button stretches into "shop now / keep scrolling" ---------- */
  NX.modules['hero-isl'] = (el) => {
    const sheet = $('[data-isl-sheet]', el), pill = $('[data-isl-pill]', el);
    if (!sheet || !pill || !NX.island) return;
    const isl = NX.island({ host: el, sheet, dir: 'down', radius: 30, wobble: pill, items: () => $$('.hi-head, .hi-opt', sheet) });
    // the hero's layers stack under the pouch; lift this one while the menu is open
    const layer = el.closest('.hf-liquid') || el.closest('.hf-layer') || el.parentElement;
    let open = false;
    const set = async (on) => {
      if (on === open) return; open = on;
      pill.setAttribute('aria-expanded', String(on));
      if (on) { layer.style.zIndex = 8; NX.lock(true); await isl.open(); }
      else { if (!document.querySelector('#CartDrawer.open')) NX.lock(false); await isl.close(); if (!open) layer.style.zIndex = ''; }
    };
    pill.addEventListener('click', () => set(!open));
    $$('a', sheet).forEach((a) => a.addEventListener('click', () => set(false)));
    document.addEventListener('pointerdown', (e) => { if (open && !el.contains(e.target)) set(false); });
    addEventListener('keydown', (e) => { if (e.key === 'Escape') set(false); });
    matchMedia('(min-width: 861px)').addEventListener('change', (m) => { if (m.matches) set(false); });
  };

  /* ---------- generic UI ---------- */
  NX.modules.accordion = (el) => {
    const single = el.dataset.single !== 'false';
    const items = $$('.acc-item', el);
    items.forEach((it) => { const q = $('.acc-q', it); q && q.addEventListener('click', () => {
      const open = !it.classList.contains('open');
      if (single) items.forEach((o) => { o.classList.remove('open'); const b = $('.acc-q', o); b && b.setAttribute('aria-expanded', 'false'); });
      it.classList.toggle('open', open); q.setAttribute('aria-expanded', open);
      if (hasGsap()) setTimeout(() => ScrollTrigger.refresh(), 750);
    }); });
  };
  NX.modules.marquee = (el) => {
    const track = $('.marquee-track', el); if (!track || track._nx) return; track._nx = 1;
    const items = Array.from(track.children);
    if (!items.length) return;
    let guard = 0;
    while (track.scrollWidth < el.clientWidth * 1.2 && guard++ < 12) items.forEach((n) => track.appendChild(n.cloneNode(true)));
    Array.from(track.children).forEach((n) => track.appendChild(Object.assign(n.cloneNode(true), { ariaHidden: 'true' })));
    const speed = +el.dataset.speed || 40; // px per second
    track.style.setProperty('--marq-dur', Math.max(8, track.scrollWidth / 2 / speed) + 's');
  };
  NX.modules.hscroll = (el) => {
    const track = $('.hs-track', el); if (!track) return;
    const bar = $('.hs-bar i', el);
    const upd = () => { const m = track.scrollWidth - track.clientWidth; bar && (bar.style.transform = `scaleX(${m > 0 ? clamp(0.12 + 0.88 * (track.scrollLeft / m), 0, 1) : 1})`); };
    track.addEventListener('scroll', upd, { passive: true }); upd();
    const step = () => { const c = track.firstElementChild; return c ? c.getBoundingClientRect().width + 16 : track.clientWidth * 0.8; };
    $$('[data-hs-prev]', el).forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' })));
    $$('[data-hs-next]', el).forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' })));
    if (fine) {
      let down = false, sx = 0, sl = 0, moved = 0;
      track.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse' || e.target.closest('.ba, input, select, textarea')) return; down = true; moved = 0; sx = e.clientX; sl = track.scrollLeft; track.classList.add('dragging'); });
      addEventListener('pointermove', (e) => { if (!down) return; moved = e.clientX - sx; track.scrollLeft = sl - moved; });
      addEventListener('pointerup', () => { if (!down) return; down = false; track.classList.remove('dragging'); });
      track.addEventListener('click', (e) => { if (Math.abs(moved) > 6) { e.preventDefault(); e.stopPropagation(); } }, true);
    }
    if (el.dataset.autoplay) {
      let t = setInterval(() => { if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 4) track.scrollTo({ left: 0, behavior: 'smooth' }); else track.scrollBy({ left: step(), behavior: 'smooth' }); }, +el.dataset.autoplay * 1000);
      track.addEventListener('pointerdown', () => clearInterval(t), { once: true });
    }
    el.addEventListener('nx:block-select', (e) => { const b = e.detail; b && track.scrollTo({ left: b.offsetLeft - 20, behavior: 'smooth' }); });
  };
  NX.modules.tabs = (el) => {
    const tabs = $$('[data-tab]', el), panels = $$('[data-panel]', el), media = $$('[data-tab-media]', el), thumb = $('[data-tab-thumb]', el);
    let cur = Math.max(0, tabs.findIndex((t) => t.classList.contains('on')));
    const place = () => { const t = tabs[cur]; if (thumb && t) { thumb.style.width = t.offsetWidth + 'px'; thumb.style.transform = `translateX(${t.offsetLeft}px)`; } };
    const select = (i) => {
      if (i === cur && el._nxInit) return; el._nxInit = 1;
      const prev = media[cur];
      if (prev && i !== cur) { prev.classList.remove('on'); prev.classList.add('was'); setTimeout(() => prev.classList.remove('was'), 1000); }
      media[i] && (void el.offsetWidth, media[i].classList.add('on'));
      tabs.forEach((t, k) => { t.classList.toggle('on', k === i); t.setAttribute('aria-selected', k === i); });
      panels.forEach((p, k) => { p.classList.toggle('on', k === i); p.hidden = false; });
      cur = i; place();
    };
    tabs.forEach((t, i) => t.addEventListener('click', () => select(i)));
    select(cur); addEventListener('resize', place); document.fonts && document.fonts.ready.then(place);
    let sx = null; const m = $('[data-tab-swipe]', el);
    m && m.addEventListener('pointerdown', (e) => (sx = e.clientX));
    m && m.addEventListener('pointerup', (e) => { if (sx === null) return; const d = e.clientX - sx; sx = null; if (Math.abs(d) > 40) select(clamp(cur + (d < 0 ? 1 : -1), 0, tabs.length - 1)); });
    el.addEventListener('nx:block-select', (e) => { const i = panels.indexOf(e.detail) > -1 ? panels.indexOf(e.detail) : tabs.indexOf(e.detail); if (i > -1) select(i); });
  };
  NX.modules.countdown = (el) => {
    if (el._nx) return; el._nx = 1;
    let end = el.dataset.end ? Date.parse(el.dataset.end) : NaN;
    if (isNaN(end)) {
      const mins = +el.dataset.evergreen || 60 * 24, key = 'nx-cd-' + (el.dataset.key || 'x');
      end = store.get(key); if (!end || end < Date.now()) { end = Date.now() + mins * 60000; store.set(key, end); }
    }
    const f = (k) => $(`[data-${k}]`, el);
    const tick = () => {
      let s = Math.max(0, Math.round((end - Date.now()) / 1000));
      const d = Math.floor(s / 86400); s -= d * 86400; const h = Math.floor(s / 3600); s -= h * 3600; const m = Math.floor(s / 60); s -= m * 60;
      const set = (k, v) => { const e = f(k); e && (e.textContent = String(v).padStart(2, '0')); };
      set('d', d); set('h', h); set('m', m); set('s', s);
      if (end > Date.now()) setTimeout(tick, 1000); else if (el.dataset.hideExpired === 'true') el.hidden = true;
    };
    tick();
  };
  NX.modules.copy = (el) => el.addEventListener('click', async () => {
    const v = el.dataset.copy;
    try { await navigator.clipboard.writeText(v); NX.toast('Copied ' + v); } catch (e) { NX.toast(v); }
  });
  NX.modules.video = (el) => {
    const v = $('video', el); if (!v) return;
    const t = $('[data-video-toggle]', el), m = $('[data-video-mute]', el);
    let want = el.dataset.autoplay !== 'false';
    v.muted = true; v.playsInline = true;
    const play = () => { const p = v.play(); p && p.catch(() => {}); };
    if (want && !reduce) new IntersectionObserver(([e]) => { if (e.isIntersecting && want) play(); else v.pause(); }, { threshold: 0.25 }).observe(el);
    t && t.addEventListener('click', () => { want = v.paused; want ? play() : v.pause(); t.classList.toggle('is-paused', !want); });
    m && m.addEventListener('click', () => { v.muted = !v.muted; m.classList.toggle('is-unmuted', !v.muted); if (!v.muted) play(); });
  };
  NX.modules['before-after'] = (el) => {
    const set = (p) => el.style.setProperty('--pos', clamp(p, 0, 100) + '%');
    let on = false;
    const move = (e) => { const r = el.getBoundingClientRect(); set(((e.clientX - r.left) / r.width) * 100); };
    el.addEventListener('pointerdown', (e) => { on = true; el.setPointerCapture(e.pointerId); move(e); });
    el.addEventListener('pointermove', (e) => on && move(e));
    el.addEventListener('pointerup', () => (on = false));
    const range = $('input[type="range"]', el); range && range.addEventListener('input', () => set(+range.value));
    set(+el.dataset.start || 50);
  };
  NX.modules.newsletter = (el) => {
    // native Shopify customer form; only adds a gentle success animation
    if (el.querySelector('.form-msg')) NX.scrollTo(el, { immediate: true });
  };

  /* ---------- signature sections ---------- */
  NX.modules['hero-flood'] = (el) => {
    const edge = $('[data-wave-edge]', el), liquid = $('[data-liquid]', el), night = $('[data-night]', el), clocks = $$('[data-clock]', el);
    if (edge && !edge.children.length) { edge.appendChild(NX.waveSVG(20, 'wave b')); edge.appendChild(NX.waveSVG(14, 'wave')); }
    const toMin = (t, d) => { const m = String(t || '').match(/(\d{1,2}):(\d{2})/); return m ? +m[1] * 60 + +m[2] : d; };
    const startM = toMin(el.dataset.start, 1384); let endM = toMin(el.dataset.end, 182); if (endM <= startM) endM += 1440;
    const setClock = (p) => {
      const t = startM + (endM - startM) * clamp(p / 0.7, 0, 1), h24 = Math.floor(t / 60) % 24, m = Math.floor(t % 60);
      const s = `${h24 % 12 || 12}:${String(m).padStart(2, '0')} ${h24 < 12 ? 'AM' : 'PM'}`;
      clocks.forEach((c) => (c.textContent = s));
    };
    setClock(0);
    const heroObj = $('[data-hero-object]', el);
    // the night glow follows the product's middle (phones place it with the height slider)
    const markCenter = () => { if (heroObj) el.style.setProperty('--obj-cy', Math.round(heroObj.offsetTop + heroObj.offsetHeight / 2) + 'px'); };
    const fitObject = () => {
      if (!heroObj) return;
      heroObj.style.width = '';
      if (innerWidth >= 860 || el.dataset.mfit !== 'true') { markCenter(); return; }
      const pin = $('[data-pin]', el) || el, H = pin.clientHeight;
      let bottom = 0;
      $$('.hf-copy', el).forEach((c) => { bottom = Math.max(bottom, c.offsetTop + c.offsetHeight); });
      const img = $('img', heroObj), ratio = img && img.naturalWidth ? img.naturalHeight / img.naturalWidth : 1.2;
      const gap = parseFloat(getComputedStyle(heroObj).bottom) || 16;
      const maxW = ((H - bottom - gap - 20) / ratio) * 0.9;
      if (maxW < heroObj.offsetWidth) heroObj.style.width = Math.max(96, Math.round(maxW)) + 'px';
      markCenter();
    };
    fitObject(); addEventListener('resize', fitObject); document.fonts && document.fonts.ready.then(fitObject);
    const oi = heroObj && $('img', heroObj); oi && !oi.complete && oi.addEventListener('load', fitObject);
    if (!liquid || !motionOn() || T.designMode || el.dataset.flood === 'false') { el.classList.add('no-flood'); return; }
    const pin = $('[data-pin]', el), off = () => innerHeight + 96;
    const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top top', end: () => '+=' + Math.round(innerHeight * (+el.dataset.length || 2.3)), pin, scrub: 0.7, anticipatePin: 1, invalidateOnRefresh: true, refreshPriority: 10, onUpdate: (s) => setClock(s.progress) } });
    tl.fromTo(liquid, { y: off }, { y: 0, ease: 'power1.inOut', duration: 0.62 }, 0)
      .fromTo(night, { y: () => -off() }, { y: 0, ease: 'power1.inOut', duration: 0.62 }, 0)
      .to($('[data-day-copy]', el), { y: -50, ease: 'none', duration: 0.5 }, 0)
      .to($('[data-hero-object]', el), { rotate: 6, scale: 1.07, ease: 'none', duration: 1 }, 0)
      .to($('[data-cue]', el), { opacity: 0, duration: 0.08 }, 0)
      .fromTo($('[data-night-extra]', el), { opacity: 0, y: 36 }, { opacity: 1, y: 0, ease: 'power2.out', duration: 0.24 }, 0.68)
      .to({}, { duration: 0.08 }, 0.92);
    const obj = $('[data-hero-object] img', el); obj && whileVisible(gsap.to(obj, { y: -16, duration: 3.2, yoyo: true, repeat: -1, ease: 'sine.inOut' }), el);
    const caps = $$('[data-hero-object] .cap', el);
    caps.forEach((c, i) => { const r0 = [-24, 32, -58, 14, 70][i % 5]; gsap.set(c, { rotation: r0 }); whileVisible(gsap.to(c, { rotation: r0 + gsap.utils.random(-30, 30), y: gsap.utils.random(-18, 18), duration: gsap.utils.random(3, 5), yoyo: true, repeat: -1, ease: 'sine.inOut' }), el); });
    if (fine) {
      const qx = caps.map((c) => gsap.quickTo(c, 'x', { duration: 1.2, ease: 'power3' }));
      el.addEventListener('pointermove', (e) => { const dx = e.clientX / innerWidth - 0.5; caps.forEach((c, i) => qx[i](dx * (30 + i * 16))); });
    }
  };
  NX.modules.blobs = (el) => {
    if (!motionOn()) return;
    $$('i', el).forEach((b, i) => {
      gsap.set(b, { xPercent: gsap.utils.random(-30, 30), yPercent: gsap.utils.random(-30, 30) });
      whileVisible(gsap.to(b, { xPercent: gsap.utils.random(-70, 70), yPercent: gsap.utils.random(-70, 70), scale: gsap.utils.random(0.8, 1.25), duration: gsap.utils.random(4, 7), yoyo: true, repeat: -1, ease: 'sine.inOut', delay: i * 0.3 }), el);
    });
  };
  /* ---------- hero: liquid glass lava lamp (WebGL metaballs) ---------- */
  const num = (v, def) => (v === undefined || v === '' || isNaN(+v) ? def : +v);
  const hexRGB = (h) => { const m = String(h || '').replace('#', '').match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i); return m ? [1, 2, 3].map((i) => parseInt(m[i], 16) / 255) : [0.7, 0.85, 0.8]; };
  const runWhenVisible = (el, start, stop) => {
    let vis = false;
    new IntersectionObserver(([e]) => { vis = e.isIntersecting; vis && !document.hidden ? start() : stop(); }, { rootMargin: '100px' }).observe(el);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : vis && start()));
  };
  const whileVisible = (tw, el) => { if (tw && el) runWhenVisible(el, () => tw.resume(), () => tw.pause()); return tw; };
  NX.modules.lava = (el) => {
    const cv = $('canvas', el);
    const gl = cv && (cv.getContext('webgl', { premultipliedAlpha: true, alpha: true, antialias: false }) || cv.getContext('experimental-webgl'));
    if (!gl) { el.style.display = 'none'; return; }
    const MAX = 12;
    const vs = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}';
    const fs = `precision highp float;
uniform vec3 uB[${MAX}];uniform vec3 uC[${MAX}];uniform int uN;uniform float uAlpha;uniform float uGloss;uniform float uBump;
void main(){
  vec2 p=gl_FragCoord.xy;float f=0.;vec3 col=vec3(0.);float ws=0.;vec2 g=vec2(0.);
  for(int i=0;i<${MAX};i++){ if(i>=uN) break; vec3 b=uB[i]; vec2 d=p-b.xy; float r2=b.z*b.z; float q=r2/(dot(d,d)+r2*.02); f+=q; float w=q*q*q; col+=uC[i]*w; ws+=w; g+=-2.*q*q/r2*d; }
  col/=max(ws,1e-5);
  float a=smoothstep(.965,1.035,f);
  vec3 n=normalize(vec3(-g*uBump,1.));
  vec3 L=normalize(vec3(-.45,.55,.7));vec3 H=normalize(L+vec3(0.,0.,1.));
  float diff=max(dot(n,L),0.);float spec=pow(max(dot(n,H),0.),70.)*uGloss*1.6;float spec2=pow(max(dot(n,normalize(vec3(.5,-.4,.75))),0.),26.)*uGloss*.35;float rim=pow(1.-n.z,1.6);
  vec3 c=col*(.72+.38*diff)*(1.-rim*.22)+vec3(1.)*(spec+spec2+rim*.22*uGloss);
  float al=a*clamp(uAlpha*(.62+.38*rim)+spec*.9,0.,1.);
  gl_FragColor=vec4(min(c,vec3(1.))*al,al);
}`;
    const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null; };
    const v = sh(gl.VERTEX_SHADER, vs), f = sh(gl.FRAGMENT_SHADER, fs);
    if (!v || !f) { el.style.display = 'none'; return; }
    const prog = gl.createProgram(); gl.attachShader(prog, v); gl.attachShader(prog, f); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { el.style.display = 'none'; return; }
    gl.useProgram(prog);
    const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'a'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const U = (n) => gl.getUniformLocation(prog, n);
    const uB = U('uB'), uC = U('uC'), uN = U('uN'), uAlpha = U('uAlpha'), uGloss = U('uGloss'), uBump = U('uBump');
    const d = el.dataset, count = clamp(+d.blobs || 6, 1, MAX - 1), size = (+d.size || 100) / 100, speed = num(d.speed, 100) / 100, follow = num(d.follow, 50) / 100;
    const cols = (d.colors || '').split(',').map(hexRGB);
    gl.uniform1f(uAlpha, clamp((+d.alpha || 85) / 100, 0, 1)); gl.uniform1f(uGloss, clamp(num(d.gloss, 70) / 100, 0, 1));
    const C = new Float32Array(MAX * 3), B = new Float32Array(MAX * 3);
    const mobile = () => innerWidth < 860;
    const blobs = Array.from({ length: count }, (_, i) => ({
      bx: 0, by: 0, r: 0.75 + Math.random() * 0.55, ph: Math.random() * 6.28, sp: 0.6 + Math.random() * 0.8,
      ax: 0.04 + Math.random() * 0.07, ay: 0.16 + Math.random() * 0.2, pull: (i % 3 === 2 ? -0.6 : 1) * (0.5 + Math.random() * 0.5), x: 0, y: 0,
    }));
    const place = () => blobs.forEach((b, i) => { const m = mobile(), k = (i + 0.5) / count; b.bx = m ? 0.1 + ((k * 2.3) % 1) * 0.8 : 0.4 + ((k * 1.7) % 1) * 0.58; b.by = m ? 0.4 + ((k * 3.1) % 1) * 0.55 : 0.12 + k * 0.78; });
    place();
    for (let i = 0; i < MAX; i++) C.set(cols[i % cols.length] || [0.7, 0.85, 0.8], i * 3);
    gl.uniform3fv(uC, C);
    let W = 0, Hh = 0, dpr = 1, base = 100;
    const resize = () => {
      const r = el.getBoundingClientRect(); dpr = Math.min(devicePixelRatio || 1, r.width > 1400 || lowPower ? 1 : 1.5);
      W = Math.max(1, Math.round(r.width * dpr)); Hh = Math.max(1, Math.round(r.height * dpr));
      cv.width = W; cv.height = Hh; gl.viewport(0, 0, W, Hh);
      base = Math.min(W, Hh) * (mobile() ? 0.12 : 0.1) * size; gl.uniform1f(uBump, base * 0.9);
    };
    resize(); new ResizeObserver(resize).observe(el);
    // pointer: blobs drift toward (or away from) it, and a small blob follows it
    const ptr = { x: 0, y: 0, tx: 0, ty: 0, on: 0, ton: 0 };
    const setPtr = (cx, cy) => { const r = el.getBoundingClientRect(); ptr.tx = (cx - r.left) * dpr; ptr.ty = (cy - r.top) * dpr; ptr.ton = cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom ? 1 : 0; };
    if (follow > 0) {
      addEventListener('pointermove', (e) => setPtr(e.clientX, e.clientY), { passive: true });
      addEventListener('touchmove', (e) => e.touches[0] && setPtr(e.touches[0].clientX, e.touches[0].clientY), { passive: true });
      document.addEventListener('pointerleave', () => (ptr.ton = 0));
    }
    let t = Math.random() * 100, last = 0, raf = 0;
    const draw = (now) => {
      if (FRAME_MS && last && now - last < FRAME_MS - 2) { if (raf) raf = requestAnimationFrame(draw); return; }
      const dt = Math.min(0.05, last ? (now - last) / 1000 : 0.016); last = now; t += dt * speed;
      ptr.x += (ptr.tx - ptr.x) * 0.08; ptr.y += (ptr.ty - ptr.y) * 0.08; ptr.on += (ptr.ton - ptr.on) * 0.05;
      const reach = Math.max(W, Hh) * 0.35;
      blobs.forEach((b, i) => {
        let x = (b.bx + Math.sin(t * 0.13 * b.sp + b.ph) * b.ax) * W;
        let y = (b.by + Math.sin(t * 0.09 * b.sp + b.ph * 1.7) * b.ay) * Hh;
        if (follow > 0 && ptr.on > 0.01) { const dx = ptr.x - x, dy = ptr.y - y, k = Math.exp(-(dx * dx + dy * dy) / (2 * reach * reach)) * follow * ptr.on * b.pull * 0.45; x += dx * k; y += dy * k; }
        b.x = b.x ? b.x + (x - b.x) * 0.12 : x; b.y = b.y ? b.y + (y - b.y) * 0.12 : y;
        const r = base * b.r * (1 + 0.12 * Math.sin(t * 0.35 * b.sp + b.ph));
        B.set([b.x, Hh - b.y, r], i * 3);
      });
      let n = count;
      if (follow > 0 && ptr.on > 0.01) { B.set([ptr.x, Hh - ptr.y, base * 0.5 * ptr.on], n * 3); n++; }
      gl.uniform3fv(uB, B); gl.uniform1i(uN, n);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (raf) raf = requestAnimationFrame(draw);
    };
    const start = () => { if (raf) return; if (!motionOn()) { draw(performance.now()); return; } last = 0; raf = requestAnimationFrame(draw); };
    const stop = () => { cancelAnimationFrame(raf); raf = 0; };
    draw(performance.now());
    runWhenVisible(el, start, stop);
  };

  /* ---------- hero night: twinkling stars ---------- */
  NX.modules.stars = (cv) => {
    const ctx = cv.getContext('2d'); if (!ctx) return;
    const d = cv.dataset, count = clamp(num(d.stars, 140), 0, 600), maxR = clamp(+d.size || 2, 0.5, 6), speed = num(d.speed, 100) / 100, color = d.color || '#ffffff', shooting = d.shooting !== 'false', drift = d.drift === 'true';
    const [cr, cg, cb] = hexRGB(color).map((x) => Math.round(x * 255));
    let W = 0, H = 0, dpr = 1;
    const mk = () => ({ x: Math.random(), y: Math.random(), r: 0.35 + Math.pow(Math.random(), 2.4) * (maxR - 0.35), a: 0.35 + Math.random() * 0.65, f1: 0.25 + Math.random() * 0.9, f2: 1.3 + Math.random() * 2.2, ph: Math.random() * 6.28, deep: Math.random() < 0.25 });
    const stars = Array.from({ length: count }, mk);
    const resize = () => { const r = cv.getBoundingClientRect(); dpr = Math.min(devicePixelRatio || 1, lowPower ? 1.5 : 2); W = Math.max(1, r.width * dpr); H = Math.max(1, r.height * dpr); cv.width = W; cv.height = H; };
    resize(); new ResizeObserver(resize).observe(cv);
    // soft glow drawn once; each bright star stamps it with its own alpha (same look as a per-star gradient)
    const glow = document.createElement('canvas'); glow.width = glow.height = 64;
    { const g2 = glow.getContext('2d'), g = g2.createRadialGradient(32, 32, 0, 32, 32, 32); g.addColorStop(0, `rgba(${cr},${cg},${cb},1)`); g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`); g2.fillStyle = g; g2.fillRect(0, 0, 64, 64); }
    const dot = `rgb(${cr},${cg},${cb})`;
    let t = 0, last = 0, raf = 0, nextShot = 3 + Math.random() * 5, shot = null;
    const draw = (now) => {
      if (FRAME_MS && last && now - last < FRAME_MS - 2) { if (raf) raf = requestAnimationFrame(draw); return; }
      const dt = Math.min(0.05, last ? (now - last) / 1000 : 0.016); last = now; t += dt * speed;
      ctx.clearRect(0, 0, W, H); ctx.fillStyle = dot;
      const py = drift ? -cv.getBoundingClientRect().top * 0.12 * dpr : 0;
      for (const s of stars) {
        // two layered waves give an irregular, natural twinkle; "deep" stars fade out completely now and then
        let k = 0.55 + 0.3 * Math.sin(t * s.f1 + s.ph) + 0.15 * Math.sin(t * s.f2 + s.ph * 2);
        if (s.deep) k *= Math.max(0, Math.sin(t * s.f1 * 0.35 + s.ph));
        const a = clamp(s.a * k, 0, 1);
        if (s.deep && a < 0.01 && Math.random() < 0.004) Object.assign(s, mk(), { deep: true });
        if (a < 0.01) continue;
        let x = s.x * W, y = s.y * H; const r = s.r * dpr;
        if (drift) { x = (((s.x + t * 0.0035 * (0.4 + s.r / maxR)) % 1) + 1) % 1 * W; y = ((((s.y * H + py * (0.4 + s.r / maxR)) % H) + H) % H); }
        if (s.r > 1.2) { ctx.globalAlpha = a * 0.35; ctx.drawImage(glow, x - r * 4, y - r * 4, r * 8, r * 8); }
        ctx.globalAlpha = a; ctx.beginPath(); ctx.arc(x, y, r, 0, 6.2832); ctx.fill();
      }
      ctx.globalAlpha = 1;
      if (shooting) {
        nextShot -= dt;
        if (!shot && nextShot <= 0) { shot = { x: 0.3 + Math.random() * 0.7, y: Math.random() * 0.35, p: 0 }; nextShot = 6 + Math.random() * 9; }
        if (shot) {
          shot.p += dt / 0.9; const p = shot.p, len = 0.16, x = (shot.x - p * 0.35) * W, y = (shot.y + p * 0.2) * H;
          const g = ctx.createLinearGradient(x, y, x + len * W * 0.87, y - len * W * 0.5);
          g.addColorStop(0, `rgba(${cr},${cg},${cb},${0.9 * (1 - p)})`); g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
          ctx.strokeStyle = g; ctx.lineWidth = 1.4 * dpr; ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + len * W * 0.87, y - len * W * 0.5); ctx.stroke();
          if (p >= 1) shot = null;
        }
      }
      if (raf) raf = requestAnimationFrame(draw);
    };
    const start = () => { if (raf) return; if (!motionOn()) { draw(performance.now()); return; } last = 0; raf = requestAnimationFrame(draw); };
    const stop = () => { cancelAnimationFrame(raf); raf = 0; };
    draw(performance.now());
    runWhenVisible(cv, start, stop);
  };
  /* ---------- alternating features: cascading reveal + progress line ---------- */
  NX.modules['alt-reveal'] = (el) => {
    const rows = $$('[data-alt-row]', el), line = $('.alt-line', el), fill = line && $('i', line);
    const placeLine = () => {
      const ns = rows.map((r) => $('[data-alt-n]', r)).filter(Boolean);
      if (!line || ns.length < 2) { line && (line.style.display = 'none'); return; }
      const top = el.getBoundingClientRect().top, a = ns[0].getBoundingClientRect(), b = ns[ns.length - 1].getBoundingClientRect();
      Object.assign(line.style, { top: a.top + a.height / 2 - top + 'px', bottom: 'auto', height: b.top - a.top + 'px', left: a.left + a.width / 2 - el.getBoundingClientRect().left - 1 + 'px' });
    };
    placeLine(); addEventListener('resize', placeLine); document.fonts && document.fonts.ready.then(placeLine);
    if (!motionOn() || T.designMode) { fill && fill.style.setProperty('--p', 1); return; }
    if (fill) ScrollTrigger.create({ trigger: line, start: 'top 65%', end: 'bottom 65%', scrub: 0.6, onUpdate: (s) => fill.style.setProperty('--p', s.progress.toFixed(4)), onRefresh: placeLine });
    const style = el.dataset.style || 'cascade';
    rows.forEach((row, i) => {
      const ic = $('[data-alt-icon]', row), media = $('[data-alt-media]', row), n = $('[data-alt-n]', row), t = $('[data-alt-t]', row), p = $('[data-alt-p]', row);
      const parts = [ic, media, n, t, p].filter(Boolean);
      if (style === 'simple') {
        gsap.set(parts, { opacity: 0, y: 30 });
        ScrollTrigger.create({ trigger: row, start: 'top 82%', once: true, onEnter: () => gsap.to(parts, { opacity: 1, y: 0, duration: 1, ease: 'expo.out', stagger: 0.08 }) });
        return;
      }
      if (style === 'slide') {
        const dir = row.classList.contains('alt-row--rev') ? 1 : -1;
        gsap.set(parts, { opacity: 0, x: 80 * dir });
        ScrollTrigger.create({ trigger: row, start: 'top 82%', once: true, onEnter: () => gsap.to(parts, { opacity: 1, x: 0, duration: 1.2, ease: 'expo.out', stagger: 0.07 }) });
        return;
      }
      // cascade: icon flips in, number pops, title wipes up, text rises
      const tl = gsap.timeline({ paused: true });
      if (ic) { gsap.set(ic, { opacity: 0, scale: 0.6, rotate: -14, transformPerspective: 700, rotationY: 50 }); tl.to(ic, { opacity: 1, scale: 1, rotate: 0, rotationY: 0, duration: 1.1, ease: 'back.out(1.6)' }, 0); }
      if (media) { gsap.set(media, { opacity: 0, clipPath: 'inset(12% 12% 12% 12% round 2rem)' }); tl.to(media, { opacity: 1, clipPath: 'inset(0% 0% 0% 0% round 2rem)', duration: 1.2, ease: 'expo.out' }, 0); }
      if (n) { gsap.set(n, { opacity: 0, scale: 0 }); tl.to(n, { opacity: 1, scale: 1, duration: 0.7, ease: 'back.out(2.4)' }, 0.1); }
      if (t) { gsap.set(t, { opacity: 0, y: 40, clipPath: 'inset(0 0 100% 0)' }); tl.to(t, { opacity: 1, y: 0, clipPath: 'inset(0 0 -20% 0)', duration: 1, ease: 'expo.out' }, 0.22); }
      if (p) { gsap.set(p, { opacity: 0, y: 24 }); tl.to(p, { opacity: 1, y: 0, duration: 0.9, ease: 'expo.out' }, 0.38); }
      ScrollTrigger.create({ trigger: row, start: 'top 80%', once: true, onEnter: () => tl.play() });
      if (ic && fine) whileVisible(gsap.to(ic, { y: -10, duration: 2.6 + i * 0.3, yoyo: true, repeat: -1, ease: 'sine.inOut', delay: 1.2 }), row);
    });
  };
  function initDecorStars(root) {
    $$('.section[data-decor="stars"]', root).forEach((sec) => {
      if (sec._stars) return; sec._stars = 1;
      const cv = document.createElement('canvas'); cv.className = 'decor-stars'; cv.setAttribute('aria-hidden', 'true');
      const area = sec.offsetWidth * sec.offsetHeight;
      cv.dataset.stars = Math.round(clamp(area / 9000, 60, 260)); cv.dataset.size = 1.8; cv.dataset.speed = 100; cv.dataset.drift = 'true';
      const dc = getComputedStyle(sec).getPropertyValue('--dc').trim(); cv.dataset.color = /^#[0-9a-f]{6}/i.test(dc) ? dc : '#ffffff';
      sec.prepend(cv); sec.classList.add('has-star-canvas');
      NX.modules.stars(cv);
    });
  }
  NX.initDecorStars = initDecorStars;
  NX.modules.floaty = (el) => { if (motionOn()) whileVisible(gsap.to(el, { y: -14, duration: 3.2, yoyo: true, repeat: -1, ease: 'sine.inOut' }), el); };
  NX.modules.stack = (el) => {
    if (!motionOn()) return;
    const cards = $$('[data-stack-card]', el);
    // cards only dim/shrink once the next card slides over them; start values are explicit,
    // because tweening from filter:none makes GSAP start at brightness(0)
    const dim = 1 - clamp((+el.dataset.dim || 0) / 100, 0, 0.9), sc = clamp((+el.dataset.scale || 94) / 100, 0.7, 1);
    cards.forEach((card, i) => {
      const next = cards[i + 1]; if (!next) return;
      gsap.fromTo(card, { scale: 1, filter: 'brightness(1)' }, { scale: sc, filter: `brightness(${dim})`, transformOrigin: '50% 0%', ease: 'none', immediateRender: false, scrollTrigger: { trigger: next, start: 'top 80%', end: 'top 25%', scrub: true } });
    });
  };
  NX.modules.gauges = (el) => {
    const tubes = $$('.tube[data-level]', el);
    if (!motionOn()) { tubes.forEach((t) => NX.setLevel(t, +t.dataset.level)); return; }
    tubes.forEach((t) => NX.setLevel(t, 0));
    ScrollTrigger.create({ trigger: el, start: 'top 75%', once: true, onEnter: () => tubes.forEach((t, i) => setTimeout(() => NX.setLevel(t, +t.dataset.level), i * 220)) });
  };
  NX.modules.labelviz = (el) => {
    const num = $('[data-lv-num]', el), cap = $('[data-lv-cap]', el), thumb = $('.lv-thumb', el), btns = $$('.lv-switch button', el);
    const vals = { compound: +el.dataset.compound || 1800, elemental: +el.dataset.elemental || 250 };
    el.style.setProperty('--ratio', (vals.elemental / vals.compound * 100).toFixed(1) + '%');
    const cur = { v: vals.compound };
    const setMode = (mode, instant) => {
      el.dataset.mode = mode;
      btns.forEach((b) => { const on = b.dataset.mode === mode; b.setAttribute('aria-selected', on); if (on && thumb) { thumb.style.width = b.offsetWidth + 'px'; thumb.style.transform = `translateX(${b.offsetLeft}px)`; } });
      cap && (cap.textContent = el.dataset[mode + 'Text'] || '');
      if (motionOn() && !instant) gsap.to(cur, { v: vals[mode], duration: 1.3, ease: 'expo.out', onUpdate: () => (num.textContent = commas(cur.v)) });
      else { cur.v = vals[mode]; num.textContent = commas(vals[mode]); }
    };
    btns.forEach((b) => b.addEventListener('click', () => setMode(b.dataset.mode)));
    setMode('compound', true);
    addEventListener('resize', () => setMode(el.dataset.mode, true));
    document.fonts && document.fonts.ready.then(() => setMode(el.dataset.mode, true));
    if (motionOn() && el.dataset.auto !== 'false') ScrollTrigger.create({ trigger: el, start: 'top 65%', once: true, onEnter: () => setTimeout(() => setMode('elemental'), 900) });
  };
  NX.modules.timeline = (el) => {
    const tube = $('.tube', el), nEl = $('[data-tl-n]', el), uEl = $('[data-tl-u]', el), phases = $$('[data-phase]', el), steps = $$('[data-step]', el);
    const ranges = phases.map((p) => [+p.dataset.from || 1, +p.dataset.to || 7]);
    const unitA = el.dataset.unit || 'of your first month', unitB = el.dataset.unitLast || 'and every night after';
    let active = -1;
    const paint = (p) => {
      const n = phases.length; if (!n) return;
      const seg = clamp(p, 0, 0.9999) * n, i = Math.floor(seg), f = seg - i, [a, b] = ranges[i];
      if (nEl) nEl.textContent = i === n - 1 && f > 0.9 && el.dataset.plus !== 'false' ? b + '+' : Math.round(a + (b - a) * f);
      if (uEl) uEl.textContent = i === n - 1 ? unitB : unitA;
      if (tube) { NX.setLevel(tube, 0.08 + 0.92 * clamp(p, 0, 1)); tube.style.setProperty('--liquid', NX.liquidColor(p * 1.15)); }
      if (i !== active) { active = i; phases.forEach((e, k) => e.classList.toggle('on', k === i)); steps.forEach((e, k) => { e.classList.toggle('on', k === i); e.classList.toggle('done', k < i); }); }
    };
    const track = $('[data-tl-track]', el);
    paint(0);
    if (motionOn() && track && !T.designMode) {
      ScrollTrigger.create({ trigger: track, start: 'top top', end: 'bottom bottom', onUpdate: (s) => paint(s.progress) });
      steps.forEach((b, i) => b.addEventListener('click', () => { const top = track.getBoundingClientRect().top + scrollY; NX.scrollTo(top + (track.offsetHeight - innerHeight) * ((i + 0.5) / phases.length), { offset: 0 }); }));
    } else {
      el.classList.add('is-static'); paint(1);
      steps.forEach((b, i) => b.addEventListener('click', () => { phases.forEach((e, k) => e.classList.toggle('on', k === i)); steps.forEach((e, k) => e.classList.toggle('on', k === i)); }));
    }
    el.addEventListener('nx:block-select', (e) => { const i = phases.indexOf(e.detail); if (i > -1) { phases.forEach((x, k) => x.classList.toggle('on', k === i)); } });
  };
  NX.modules.reviews = (el) => {
    const slides = $$('[data-review]', el), photos = $$('[data-review-photo]', el), avs = $$('[data-review-av]', el), bar = $('.rv-progress i', el);
    if (!slides.length) return;
    let cur = -1, auto = el.dataset.autoplay !== 'false', tw;
    const fixed = +el.dataset.seconds || 0;
    if (!auto && bar) bar.parentElement.hidden = true;
    // "original photo shape": the frame follows the photo of the review on screen
    const shapeTo = (i) => { const ar = photos[i] && +photos[i].dataset.ar; if (ar) el.style.setProperty('--rv-ar', ar); };
    const go = (i) => {
      i = (i + slides.length) % slides.length; if (i === cur) return;
      const prev = photos[cur];
      if (prev) { prev.classList.remove('on'); prev.classList.add('was'); setTimeout(() => prev.classList.remove('was'), 1100); }
      photos[i] && (void el.offsetWidth, photos[i].classList.add('on'));
      slides.forEach((s, k) => s.classList.toggle('on', k === i));
      avs.forEach((a, k) => a.classList.toggle('on', k === i));
      const a = avs[i]; if (a) { const p = a.parentElement; p.scrollTo({ left: a.offsetLeft - p.clientWidth / 2 + a.offsetWidth / 2, behavior: 'smooth' }); }
      cur = i; shapeTo(i);
      if (auto && motionOn() && bar) {
        tw && tw.kill(); gsap.set(bar, { scaleX: 0 });
        const len = (slides[i].textContent || '').length;
        tw = gsap.to(bar, { scaleX: 1, duration: fixed || clamp(len / 28, 5, 12), ease: 'none', onComplete: () => go(cur + 1) });
      }
    };
    const stop = () => { auto = false; tw && tw.kill(); bar && motionOn() && gsap.to(bar, { scaleX: 0, duration: 0.4 }); };
    avs.forEach((a, i) => a.addEventListener('click', () => { stop(); go(i); }));
    $$('[data-rv-prev]', el).forEach((b) => b.addEventListener('click', () => { stop(); go(cur - 1); }));
    $$('[data-rv-next]', el).forEach((b) => b.addEventListener('click', () => { stop(); go(cur + 1); }));
    // swipe (touch or mouse drag) on the photo or the quote to change review
    const swipeZones = [...new Set([photos[0] && photos[0].parentElement, slides[0] && slides[0].parentElement].filter(Boolean))];
    swipeZones.forEach((zone) => {
      zone.style.touchAction = 'pan-y'; zone.classList.add('rv-swipe');
      let sx = 0, sy = 0, dx = 0, on = false, horiz = null;
      const cur3 = () => photos[cur];
      zone.addEventListener('pointerdown', (e) => { if (e.button > 0) return; on = true; horiz = null; sx = e.clientX; sy = e.clientY; dx = 0; });
      zone.addEventListener('pointermove', (e) => {
        if (!on) return;
        dx = e.clientX - sx; const dy = e.clientY - sy;
        if (horiz === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) horiz = Math.abs(dx) > Math.abs(dy);
        if (horiz) { const p = cur3(); if (p) p.style.transform = `translateX(${dx * 0.35}px) rotate(${dx * 0.02}deg)`; }
      });
      const end = () => {
        if (!on) return; on = false;
        const p = cur3(); if (p) p.style.transform = '';
        if (horiz && Math.abs(dx) > 45) { el.classList.add('swiped'); stop(); go(cur + (dx < 0 ? 1 : -1)); }
      };
      zone.addEventListener('pointerup', end); zone.addEventListener('pointercancel', end); zone.addEventListener('pointerleave', end);
    });
    go(0);
    if (motionOn()) ScrollTrigger.create({ trigger: el, start: 'top bottom', end: 'bottom top', onToggle: (s) => { if (tw && auto) s.isActive ? tw.resume() : tw.pause(); } });
    el.addEventListener('nx:block-select', (e) => { const i = slides.indexOf(e.detail); if (i > -1) { stop(); go(i); } });
  };
  NX.modules.pouch3d = (el) => {
    const inner = $('.p3d-inner', el); if (!inner) return;
    let angle = 0, drag = false, sx = 0, start = 0, tx = 0, ty = 0, idle = true;
    const apply = () => (inner.style.transform = `rotateX(${ty}deg) rotateY(${angle + tx}deg)`);
    const snap = () => { const to = Math.round(angle / 180) * 180; if (hasGsap()) { const o = { a: angle }; gsap.to(o, { a: to, duration: 1.1, ease: 'elastic.out(1,.6)', onUpdate: () => { angle = o.a; apply(); } }); } else { angle = to; apply(); } };
    el.addEventListener('pointerdown', (e) => { drag = true; idle = false; sx = e.clientX; start = angle; el.setPointerCapture(e.pointerId); el.classList.add('grab'); });
    el.addEventListener('pointermove', (e) => { if (drag) { angle = start + (e.clientX - sx) * 0.6; apply(); } else if (fine) { const r = el.getBoundingClientRect(); tx = ((e.clientX - r.left) / r.width - 0.5) * 14; ty = -((e.clientY - r.top) / r.height - 0.5) * 10; apply(); } });
    const up = () => { if (!drag) return; drag = false; el.classList.remove('grab'); snap(); };
    el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
    el.addEventListener('pointerleave', () => { tx = 0; ty = 0; if (!drag) apply(); });
    $$('[data-flip]', el.parentElement).forEach((b) => b.addEventListener('click', () => { idle = false; const back = Math.round(angle / 180) % 2 !== 0; if ((b.dataset.flip === 'back') !== back) angle = Math.round(angle / 180) * 180 + 180; snap(); }));
    if (motionOn()) { const o = { a: 0 }; const tw = whileVisible(gsap.to(o, { a: 1, duration: 2.4, ease: 'sine.inOut', yoyo: true, repeat: -1, onUpdate: () => { if (idle && !drag) { angle = -14 + o.a * 28; apply(); } } }), el); el.addEventListener('pointerdown', () => tw.kill(), { once: true }); }
    apply();
  };
  NX.modules.reserve = (el) => {
    const drains = $$('[data-drain]', el), tube = $('.tube', el), card = $('[data-reserve-card]', el);
    const T_ = (el.dataset.times || '2:00,2:20,2:40,3:00,3:00').split(','), S = (el.dataset.status || '').split('|'), L = (el.dataset.levels || 'Full,High,Half,Low,Almost empty').split(',');
    const state = () => {
      const n = drains.filter((d) => d.getAttribute('aria-pressed') === 'true').length, k = Math.round(n / Math.max(1, drains.length) * 4);
      NX.setLevel(tube, 1 - n / Math.max(1, drains.length) * 0.8);
      tube.style.setProperty('--liquid', NX.liquidColor(1 - n / Math.max(1, drains.length)));
      const t = $('[data-r-time]', el), s = $('[data-r-status]', el), p = $('[data-r-level]', el);
      t && (t.textContent = T_[k] || T_[T_.length - 1]); s && S[k] && (s.textContent = S[k]); p && (p.textContent = L[k] || '');
      card && card.classList.toggle('alarm', k >= 3);
    };
    drains.forEach((d) => d.addEventListener('click', () => { d.setAttribute('aria-pressed', d.getAttribute('aria-pressed') !== 'true'); state(); }));
    const refill = $('[data-refill]', el); refill && refill.addEventListener('click', () => { drains.forEach((d) => d.setAttribute('aria-pressed', 'false')); state(); });
    state();
  };
  NX.modules.ring = (el) => {
    const c = $('circle[data-ring]', el); if (!c) return;
    const C = 2 * Math.PI * (+c.getAttribute('r'));
    c.style.strokeDasharray = C;
    if (!motionOn()) { c.style.strokeDashoffset = 0; return; }
    c.style.strokeDashoffset = C;
    ScrollTrigger.create({ trigger: el, start: 'top 80%', once: true, onEnter: () => gsap.to(c, { strokeDashoffset: 0, duration: 2.6, ease: 'expo.out' }) });
  };
  NX.modules.physics = (el) => {
    const canvas = $('canvas', el); if (!canvas || reduce || T.designMode) { el.classList.add('no-physics'); return; }
    const boot = () => {
      if (!window.Matter) { el.classList.add('no-physics'); return; }
      const { Engine, World, Bodies, Body, Sleeping } = Matter;
      const ctx = canvas.getContext('2d'), dpr = Math.min(devicePixelRatio || 1, lowPower ? 1.5 : 2);
      let W = 0, H = 0, walls = [], bodies = [], running = false, spawned = false, last = 0;
      const engine = Engine.create({ gravity: { y: 1.1 }, enableSleeping: true });
      const COUNT = +el.dataset.capsules || 90, mobile = innerWidth < 700, cw = mobile ? 40 : 58, ch = mobile ? 16 : 23;
      const colA = getComputedStyle(html).getPropertyValue('--c-green').trim() || '#2F5D50', colB = getComputedStyle(html).getPropertyValue('--c-cream').trim() || '#FAF6ED';
      const size = () => {
        const r = canvas.getBoundingClientRect(); W = r.width; H = r.height; canvas.width = W * dpr; canvas.height = H * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        walls.forEach((w) => World.remove(engine.world, w));
        walls = [Bodies.rectangle(W / 2, H + 40, W * 2, 80, { isStatic: true }), Bodies.rectangle(-40, H / 2, 80, H * 4, { isStatic: true }), Bodies.rectangle(W + 40, H / 2, 80, H * 4, { isStatic: true })];
        World.add(engine.world, walls);
      };
      size(); addEventListener('resize', () => { size(); wake(); });
      const spawn = () => { if (spawned) return; spawned = true; for (let i = 0; i < COUNT; i++) setTimeout(() => { const b = Bodies.rectangle(cw + Math.random() * (W - cw * 2), -40 - Math.random() * 120, cw, ch, { chamfer: { radius: ch / 2 }, restitution: 0.35, friction: 0.25, frictionAir: 0.008, angle: Math.random() * Math.PI }); Body.setAngularVelocity(b, (Math.random() - 0.5) * 0.25); bodies.push(b); World.add(engine.world, b); }, i * 38); };
      const ptr = { x: -999, y: -999, on: false };
      canvas.addEventListener('pointermove', (e) => { const r = canvas.getBoundingClientRect(); ptr.x = e.clientX - r.left; ptr.y = e.clientY - r.top; ptr.on = e.pointerType === 'mouse'; });
      canvas.addEventListener('pointerleave', () => (ptr.on = false));
      const shake = (x, y) => bodies.forEach((b) => { const dx = b.position.x - x, dy = b.position.y - y, d = Math.hypot(dx, dy) || 1; if (d < 260) { Sleeping.set(b, false); wake(); } if (d < 260) Body.applyForce(b, b.position, { x: (dx / d) * 0.012 * (1 - d / 260), y: -0.016 * (1 - d / 260) }); });
      canvas.addEventListener('pointerdown', (e) => { const r = canvas.getBoundingClientRect(); shake(e.clientX - r.left, e.clientY - r.top); });
      // the capsule is painted once into a sprite, then stamped for every body
      const sprite = document.createElement('canvas'); sprite.width = Math.ceil(cw * dpr); sprite.height = Math.ceil(ch * dpr);
      { const s = sprite.getContext('2d'); s.scale(dpr, dpr); s.beginPath(); s.roundRect(0, 0, cw, ch, ch / 2); s.clip();
        s.fillStyle = colA; s.fillRect(0, 0, cw / 2, ch); s.fillStyle = colB; s.fillRect(cw / 2, 0, cw / 2, ch);
        const g = s.createLinearGradient(0, 0, 0, ch); g.addColorStop(0, 'rgba(255,255,255,.4)'); g.addColorStop(0.45, 'rgba(255,255,255,0)'); g.addColorStop(1, 'rgba(0,0,0,.28)');
        s.fillStyle = g; s.fillRect(0, 0, cw, ch); }
      const draw = (b) => {
        const c = Math.cos(b.angle) * dpr, s = Math.sin(b.angle) * dpr;
        ctx.setTransform(c, s, -s, c, b.position.x * dpr, b.position.y * dpr); ctx.drawImage(sprite, -cw / 2, -ch / 2, cw, ch);
      };
      let visible = false, settled = 0;
      const frame = (t) => {
        if (!running) return;
        const dt = Math.min(32, t - last || 16); last = t; if (ptr.on) shake(ptr.x, ptr.y + 30); Engine.update(engine, dt);
        ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, canvas.width, canvas.height); bodies.forEach(draw); ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        // once every capsule is asleep the picture can't change: stop until something wakes them
        settled = spawned && bodies.length >= COUNT && !ptr.on && bodies.every((b) => b.isSleeping) ? settled + 1 : 0;
        if (settled > 30) { running = false; return; }
        requestAnimationFrame(frame);
      };
      function wake() { if (!running && visible) { running = true; settled = 0; last = 0; requestAnimationFrame(frame); } }
      canvas.addEventListener('pointerenter', wake);
      new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) { spawn(); wake(); } else running = false; }, { threshold: 0.2 }).observe(el);
    };
    if (window.Matter) boot();
    else { const s = document.createElement('script'); s.src = 'https://cdn.jsdelivr.net/npm/matter-js@0.19.0/build/matter.min.js'; s.onload = boot; s.onerror = () => el.classList.add('no-physics'); document.head.appendChild(s); }
  };
  NX.modules['ab-slider'] = (el) => {
    const slides = $('.ab-slide', el); if (slides.length < 2) return;
    let i = 0; const dur = (+el.dataset.duration || 4) * 1000;
    setInterval(() => {
      const cur = slides[i]; i = (i + 1) % slides.length; const nx = slides[i];
      cur.classList.add('out'); cur.classList.remove('on');
      nx.classList.remove('out'); nx.classList.add('on');
      setTimeout(() => cur.classList.remove('out'), 700);
    }, dur);
  };
  NX.modules.inview = (el) => {
    if (!('IntersectionObserver' in window)) return el.classList.add('is-in');
    new IntersectionObserver(([e], io) => { if (e.isIntersecting) { el.classList.add('is-in'); io.disconnect(); } }, { threshold: +el.dataset.threshold || 0.25 }).observe(el);
  };
  NX.modules['viewers'] = (el) => {
    const n = $('[data-viewers]', el); if (!n) return;
    const min = +el.dataset.min || 12, max = +el.dataset.max || 38;
    const set = () => (n.textContent = Math.floor(min + Math.random() * (max - min)));
    set(); setInterval(set, 7000);
  };

  /* ---------- Your 3AM, Understood: the night pop-out, loaded the first time it's needed ---------- */
  const Night = (() => {
    let p = null;
    const cfg = () => { try { return JSON.parse($('#NightConfig').textContent); } catch (e) { return null; } };
    const add = (tag, attrs) => new Promise((res, rej) => { const e = Object.assign(document.createElement(tag), attrs); e.onload = res; e.onerror = rej; document.head.appendChild(e); });
    const load = () => p || (p = (async () => {
      const c = cfg(); if (!c) throw new Error('no night config');
      const css = add('link', { rel: 'stylesheet', href: c.css });
      for (const src of c.assets) await add('script', { src, async: false });
      await css;
      return c;
    })().catch((e) => { p = null; throw e; }));
    const open = async (source) => { try { const c = await load(); NX.night && NX.night.open(source, c); } catch (e) { console.error('[nocturyl] 3am', e); } };
    return { load, open };
  })();
  NX.openNight = Night.open;
  const isNightLink = (a) => { if (!a || !a.href) return false; const u = new URL(a.href, location.href); return u.hash === '#3am' && u.pathname === location.pathname; };
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-night-open]') || e.target.closest('a[href*="#3am"]');
    if (!t || (t.tagName === 'A' && !t.hasAttribute('data-night-open') && !isNightLink(t))) return;
    e.preventDefault(); e.stopPropagation();
    Night.open(t.closest('[data-night-door]') || t);
  }, true);
  // warm the files up as soon as there is a way in on the page
  const warmNight = () => { if ($('[data-night-open], a[href*="#3am"]')) (window.requestIdleCallback || setTimeout)(() => Night.load().catch(() => {}), { timeout: 3000 }); };
  // deep link: any page with #3am or ?3am opens the pop-out (ads, emails, the advertorial)
  const nightDeepLink = () => { if (location.hash === '#3am' || new URLSearchParams(location.search).has('3am')) setTimeout(() => Night.open(null), 600); };

  /* ---------- the 3AM Walkthrough hands her over with ?pick=1|2|3: say so near the buy box and select that bundle ---------- */
  function walkPick() {
    const n = +new URLSearchParams(location.search).get('pick');
    const root = $('[data-product-root]');
    if (!(n >= 1 && n <= 3) || !root) return;
    let text = 'Your pick from the Walkthrough: [pick]';
    try { text = JSON.parse($('#NightConfig').textContent).pickNote || text; } catch (e) { /* default */ }
    const note = document.createElement('p');
    note.className = 'pd-walk-pick';
    note.innerHTML = '<svg class="ico" viewBox="0 0 24 24" aria-hidden="true"><use href="#i-moon"/></svg><span></span>';
    $('span', note).textContent = text.split('[pick]').join(n + ' pouch' + (n > 1 ? 'es' : ''));
    let tries = 0;
    const t = setInterval(() => {
      const opts = $$('moonbundle-component .moonbundle-option'), box = $('moonbundle-component');
      if (box && !note.isConnected) box.parentNode.insertBefore(note, box);
      if (opts[n - 1]) { clearInterval(t); if (!opts[n - 1].classList.contains('moonbundle-option-active')) opts[n - 1].click(); }
      else if (++tries > 40) { clearInterval(t); if (!note.isConnected) { const f = $('form[action*="/cart/add"]', root); f && f.parentNode.insertBefore(note, f); } }
    }, 250);
  }

  /* ---------- boot ---------- */
  function init(root = document) {
    initTubes(root);
    const list = root.matches && root.matches('[data-module]') ? [root, ...$$('[data-module]', root)] : $$('[data-module]', root);
    list.forEach((el) => {
      el.dataset.module.split(' ').forEach((name) => {
        const key = '_m_' + name;
        if (el[key] || !NX.modules[name]) return;
        el[key] = 1;
        try { NX.modules[name](el); } catch (err) { console.error('[nocturyl]', name, err); }
      });
    });
    initMotion(root);
    initDecorStars(root);
    initMagnetic(root);
  }
  NX.init = init;
  const offscreen = 'IntersectionObserver' in window ? new IntersectionObserver((list) => list.forEach((e) => e.target.classList.toggle('nx-off', !e.isIntersecting)), { rootMargin: '150px 0px' }) : null;
  // fixed UI (header, sticky bar, cart drawer) lives in zero-height section wrappers, so those are never paused this way
  const watchable = (s) => s.classList && s.classList.contains('shopify-section') && !s.querySelector('.site-header, .satc, #CartDrawer');
  const watchSections = (root = document) => offscreen && $$('.shopify-section', root).filter(watchable).forEach((s) => offscreen.observe(s));
  function boot() {
    setupScroll();
    if (motionOn() && !T.designMode) html.classList.add('js-motion');
    init(document);
    watchSections(document);
    walkPick();
    warmNight(); nightDeepLink();
    if (hasGsap()) {
      let rt; const settle = () => { clearTimeout(rt); rt = setTimeout(() => ScrollTrigger.refresh(), 300); };
      if ('ResizeObserver' in window) new ResizeObserver(settle).observe(document.body);
      document.fonts && document.fonts.ready.then(settle);
      addEventListener('load', settle);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  /* ---------- theme editor ---------- */
  document.addEventListener('shopify:section:load', (e) => { init(e.target); offscreen && watchable(e.target) && offscreen.observe(e.target); hasGsap() && ScrollTrigger.refresh(); });
  document.addEventListener('shopify:section:unload', (e) => { if (hasGsap()) ScrollTrigger.getAll().forEach((s) => { if (s.trigger && e.target.contains(s.trigger)) s.kill(true); }); });
  document.addEventListener('shopify:block:select', (e) => {
    const host = e.target.closest('[data-module]');
    host && host.dispatchEvent(new CustomEvent('nx:block-select', { detail: e.target }));
  });
  document.addEventListener('shopify:section:select', (e) => { if (e.target.querySelector('#CartDrawer')) Cart.open(); });
  document.addEventListener('shopify:section:deselect', (e) => { if (e.target.querySelector('#CartDrawer')) Cart.close(); });
})();
